import { modeLabel, MODE_POST, MODE_CP, isExternalOwner } from "@/lib/businessModel";
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Tag, SlidersHorizontal, Search, Loader2, Save, X,
  CheckCircle2, AlertTriangle, RotateCcw, Wallet, ImagePlus, Building2,
  ShoppingCart, Boxes, Plus, Info, Wrench, ChevronDown, LayoutDashboard, ReceiptText, ArrowRight, Pencil, Trash2, Printer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { AdminNavigation } from "@/components/AdminNavigation";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";
import { useAuth } from "@/context/AuthContext";
import { ContractButton } from "@/pages/ContractActions";
import { SalesTab } from "@/components/SalesTab";
import { ManualTradeInTab } from "@/components/ManualTradeInTab";
import { SimpleSaleTab } from "@/components/SimpleSaleTab";
import { NewIPhonesTab } from "@/components/NewIPhonesTab";
import { WarrantyContractsTab } from "@/components/WarrantyContractsTab";
import { RetirosPanel } from "@/components/RetirosPanel";
import { AccessoriesTab } from "@/components/AccessoriesTab";
import { ShipmentsAdminTab } from "@/components/ShipmentsAdminTab";
import { DirectPurchaseDialog } from "@/components/DirectPurchaseDialog";
import { ModelCombobox } from "@/components/ModelCombobox";
import { HomeDashboard } from "@/components/HomeDashboard";
import { InvestmentsAdminTab } from "@/components/InvestmentsAdminTab";
import { ConsignadoresTab } from "@/components/ConsignadoresTab";
import { ApprovalsTab } from "@/components/ApprovalsTab";
import { printUsedTicket } from "@/lib/printTicket";
import { EditInventoryDialog } from "@/components/EditInventoryDialog";
import { DeleteDeviceDialog } from "@/components/DeleteDeviceDialog";
import { DevicePhotos } from "@/components/DevicePhotos";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const hasTime = typeof iso === "string" && iso.length > 10;
    const d = new Date(hasTime ? iso : iso + "T00:00:00");
    const b = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    return hasTime ? `${b} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : b;
  } catch { return iso; }
};

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const perms = admin?.permissions || [];
  const can = (p) => admin?.role === "admin" || perms.includes(p);
  const tabDefs = [["prices", "prices:view"], ["rules", "rules:view"], ["quotes", "quotes:view"], ["purchases", "purchases:view"], ["reports", "reports:view"], ["users", "users:manage"], ["contract", "contract:config"]];
  const defaultTab = (tabDefs.find(([, p]) => can(p)) || ["quotes"])[0];
  const [tab, setTab] = useState("inicio");
  const navItems = [
    ["inicio", "Inicio", true],
    ["approvals", "Por aprobar", can("approvals:manage")],
    ["prices", "Precios", can("prices:view")],
    ["rules", "Reglas", can("rules:view")],
    ["quotes", "Cotizaciones", can("quotes:view")],
    ["manual-tradein", "Trade-In manual", can("quotes:manage") && can("inventory:view")],
    ["purchases", "Compra directa", can("purchases:view")],
    ["inventory", "iPhone Usados", can("inventory:view")],
    ["new-iphones", "iPhone Nuevos", can("new_inventory:view")],
    ["accessories", "Accesorios", can("accessories:view")],
    ["shipments", "Recepción de mercancía", can("shipments:receive")],
    ["withdrawals", "Retiros", can("withdrawals:view") || can("cash_withdrawals:view")],
    ["simple-sale", "Venta simple", can("sales:manage")],
    ["warranty", "Garantías y Contratos", can("sales:manage")],
    ["sales", "Ventas", can("reports:view")],
    ["reports", "Informes", can("reports:view")],
    ["users", "Usuarios", can("users:manage")],
    ["investments", "Inversionistas", can("investments:view")],
    ["consignadores", "Consignadores", can("owners:manage")],
    ["contract", "Contrato", can("contract:config")],
  ].filter((x) => x[2]);
  const navigate = useNavigate();
  const quickActions = [["simple-sale", "Venta simple", ShoppingCart], ["manual-tradein", "Trade-In", RotateCcw], ["inventory", "iPhone Usados", Boxes]].filter(([id]) => navItems.some(([nid]) => nid === id));

  const doLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  // Vendedor/Manager NO ven el panel Admin completo: interfaz operativa simplificada.
  if (admin?.role !== "admin") return <StaffPanel />;

  return (
    <div className="min-h-screen bg-[#f7f7f8] text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-slate-200/80 shadow-sm">
        <div className="px-4 sm:px-6 h-16 flex items-center justify-between">
          <button type="button" onClick={() => setTab("inicio")} data-testid="header-logo-home" aria-label="Ir a Inicio" className="flex items-center gap-2.5 rounded-lg transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400">
            <img src="/puntocell-logo.png" alt="PuntoCell" className="h-8 w-auto max-w-[150px] object-contain" />
            <span className="font-bold tracking-tight text-lg text-slate-900 border-l border-slate-200 pl-2.5">Admin</span>
          </button>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-500">{admin?.email}</span>
            <Button variant="outline" onClick={doLogout} data-testid="logout-button" className="h-10 rounded-lg border-slate-200 text-slate-700 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200">
              <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <AdminNavigation items={navItems} value={tab} onChange={setTab} />
      <main className="md:pl-64 pb-[calc(76px+env(safe-area-inset-bottom))] md:pb-0">
        <div className="mx-auto max-w-4xl px-5 py-7">
        <Tabs value={tab} onValueChange={setTab}>

          <TabsContent value="inicio"><HomeDashboard onNavigate={setTab} allowed={new Set(navItems.map((x) => x[0]))} /></TabsContent>
          {can("approvals:manage") && <TabsContent value="approvals"><ApprovalsTab /></TabsContent>}
          {can("prices:view") && <TabsContent value="prices"><PricesTab quickActions={quickActions} onNavigate={setTab} /></TabsContent>}
          {can("rules:view") && <TabsContent value="rules"><RulesTab /></TabsContent>}
          {can("quotes:view") && <TabsContent value="quotes"><QuotesTab canManage={can("quotes:manage")} /></TabsContent>}
          {can("quotes:manage") && can("inventory:view") && <TabsContent value="manual-tradein"><ManualTradeInTab /></TabsContent>}
          {can("purchases:view") && <TabsContent value="purchases"><PurchasesTab canManage={can("purchases:manage")} /></TabsContent>}
          {can("inventory:view") && <TabsContent value="inventory"><InventoryTab canManage={can("inventory:manage")} canPurchase={can("purchases:manage")} /></TabsContent>}
          {can("new_inventory:view") && <TabsContent value="new-iphones"><NewIPhonesTab isAdmin={admin?.role === "admin"} canManage={can("new_inventory:manage")} /></TabsContent>}
          {can("accessories:view") && <TabsContent value="accessories"><AccessoriesTab isAdmin={admin?.role === "admin"} canManage={can("accessories:manage")} /></TabsContent>}
          {can("shipments:receive") && <TabsContent value="shipments"><ShipmentsAdminTab /></TabsContent>}
          {(can("withdrawals:view") || can("cash_withdrawals:view")) && <TabsContent value="withdrawals"><RetirosPanel canViewMerch={can("withdrawals:view")} canConfirmMerch={can("withdrawals:confirm")} canViewCash={can("cash_withdrawals:view")} canConfirmCash={can("cash_withdrawals:confirm")} /></TabsContent>}
          {can("sales:manage") && <TabsContent value="simple-sale"><SimpleSaleTab /></TabsContent>}
          {can("sales:manage") && <TabsContent value="warranty"><WarrantyContractsTab /></TabsContent>}
          {can("reports:view") && <TabsContent value="sales"><SalesTab /></TabsContent>}
          {can("reports:view") && <TabsContent value="reports"><ReportsTab /></TabsContent>}
          {can("users:manage") && <TabsContent value="users"><UsersTab /></TabsContent>}
          {can("investments:view") && <TabsContent value="investments"><InvestmentsAdminTab /></TabsContent>}
          {can("owners:manage") && <TabsContent value="consignadores"><ConsignadoresTab /></TabsContent>}
          {can("contract:config") && <TabsContent value="contract"><ContractConfigTab /></TabsContent>}
        </Tabs>
        </div>
      </main>
    </div>
  );
}

/* ------------------------------- PRECIOS ------------------------------- */
const round2 = (x) => Math.round(x * 100) / 100;
const slug = (s) => s.replace(/\s+/g, "-").toLowerCase();

function ModelImageManager({ model }) {
  const s = slug(model);
  const base = process.env.REACT_APP_BACKEND_URL;
  const [bust, setBust] = useState(Date.now());
  const [stage, setStage] = useState(0); // 0=custom, 1=estatica, 2=sin imagen
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const src = stage === 0 ? `${base}/api/model-image/${encodeURIComponent(model)}?t=${bust}` : stage === 1 ? `/phones/${s}.jpg` : null;

  const save = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("model", model);
      fd.append("file", file);
      await adminApi.post("/admin/model-image", fd);
      setFile(null); setStage(0); setBust(Date.now());
      toast.success(`Imagen de ${model} actualizada`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir la imagen");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-3" data-testid={`model-image-${s}`}>
      <div className="h-16 w-16 shrink-0 rounded-xl border border-zinc-200 bg-white grid place-items-center overflow-hidden">
        {src ? (
          <img src={src} alt={model} className="h-full w-full object-contain" onError={() => setStage((x) => x + 1)} />
        ) : (
          <span className="text-[9px] font-medium text-zinc-400 text-center px-1" data-testid={`model-image-empty-${s}`}>Sin imagen</span>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <input ref={inputRef} type="file" accept="image/png" className="hidden" data-testid={`model-image-input-${s}`} onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} className="h-8 rounded-lg text-xs" data-testid={`model-image-change-${s}`}>
          <ImagePlus className="w-3.5 h-3.5 mr-1" /> {file ? "Elegir otra" : "Cambiar imagen"}
        </Button>
        {file && <span className="text-[10px] text-zinc-500 max-w-[150px] truncate" data-testid={`model-image-filename-${s}`}>{file.name}</span>}
        <Button type="button" onClick={save} disabled={!file || saving} className="h-8 rounded-lg text-xs bg-zinc-900 hover:bg-zinc-800" data-testid={`model-image-save-${s}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

const InvThumb = ({ model, color, className = "h-12 w-12" }) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  const [stage, setStage] = useState(0);
  const src = stage === 0 ? `${base}/api/model-image/${encodeURIComponent(model)}${color ? `?color=${encodeURIComponent(color)}` : ""}` : stage === 1 ? `/phones/${slug(model)}.jpg` : null;
  return (
    <div className={`${className} shrink-0 rounded-lg border border-zinc-200 bg-white grid place-items-center overflow-hidden`} data-testid="inv-thumb">
      {src ? <img src={src} alt={model} className="h-full w-full object-contain" onError={() => setStage((x) => x + 1)} /> : <span className="text-[8px] font-medium text-zinc-400 text-center px-0.5">Sin imagen</span>}
    </div>
  );
};

// Chip de subida de imagen por MODELO + COLOR (banco central compartido usados/nuevos).
function ColorImageChip({ model, color, hex }) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const [bust, setBust] = useState(Date.now());
  const [exists, setExists] = useState(true);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const src = `${base}/api/model-image/${encodeURIComponent(model)}?color=${encodeURIComponent(color)}&strict=1&t=${bust}`;
  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "image/png") { toast.error("Solo se permite PNG"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("model", model); fd.append("color", color); fd.append("file", file);
      await adminApi.post("/admin/model-image", fd);
      setExists(true); setBust(Date.now());
      toast.success(`Imagen actualizada · ${model} · ${color}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo subir la imagen"); }
    finally { setSaving(false); }
  };
  return (
    <div className="w-[108px] rounded-xl border border-zinc-200 p-2 flex flex-col items-center gap-1.5" data-testid={`color-image-${slug(model)}-${slug(color)}`}>
      <div className="h-14 w-14 rounded-lg border border-zinc-100 bg-white grid place-items-center overflow-hidden">
        {exists ? <img src={src} alt={color} className="h-full w-full object-contain" onError={() => setExists(false)} />
          : <span className="h-6 w-6 rounded-full border border-zinc-300" style={{ background: hex || "#eee" }} />}
      </div>
      <span className="text-[10px] text-zinc-700 text-center leading-tight">{color}</span>
      <span className={`text-[9px] font-medium ${exists ? "text-emerald-600" : "text-zinc-400"}`}>{exists ? "Con imagen" : "Sin imagen"}</span>
      <input ref={inputRef} type="file" accept="image/png" className="hidden" data-testid={`color-image-input-${slug(model)}-${slug(color)}`} onChange={(e) => upload(e.target.files?.[0])} />
      <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={saving} className="h-7 w-full rounded-lg text-[10px] px-1" data-testid={`color-image-btn-${slug(model)}-${slug(color)}`}>
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : (exists ? "Reemplazar" : "Subir")}
      </Button>
    </div>
  );
}

function ModelColorImages({ model, colors }) {
  if (!colors || colors.length === 0) return null;
  return (
    <div className="mt-1" data-testid={`model-colors-${slug(model)}`}>
      <p className="text-[11px] font-medium text-zinc-500 mb-2">🎨 Imágenes por color <span className="text-zinc-400 font-normal">(se aplican a todas las capacidades)</span></p>
      <div className="flex flex-wrap gap-2">
        {colors.map((c) => <ColorImageChip key={c.name} model={model} color={c.name} hex={c.hex} />)}
      </div>
    </div>
  );
}

function ImageAuditPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await adminApi.get("/admin/image-audit"); setData(data); }
    catch { toast.error("No se pudo ejecutar la auditoría"); }
    finally { setBusy(false); }
  };
  const Row = ({ title, a }) => (
    <div className="rounded-xl border border-zinc-200 p-3" data-testid={`audit-${slug(title)}`}>
      <p className="font-semibold text-sm text-zinc-900 mb-1">{title} · {a.total} equipos</p>
      <ul className="text-xs text-zinc-600 space-y-0.5">
        <li>✅ Modelo+Color con imagen: <b className="text-zinc-900">{a.valid_with_image}</b></li>
        <li>🟡 Color válido, falta imagen: <b className="text-zinc-900">{a.valid_missing_image}</b></li>
        <li>⚪ Sin color: <b className="text-zinc-900">{a.no_color}</b></li>
        <li>🔴 Color no reconocido: <b className="text-zinc-900">{a.unrecognized_color}</b></li>
      </ul>
    </div>
  );
  return (
    <div className="mb-6 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="image-audit-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-zinc-900 text-sm">Auditoría de imágenes (Modelo + Color)</p>
          <p className="text-xs text-zinc-500">Revisa usados y nuevos frente al banco de imágenes. Solo lectura, no modifica datos.</p>
        </div>
        <Button onClick={run} disabled={busy} variant="outline" className="h-9 rounded-xl shrink-0" data-testid="image-audit-run">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Auditar"}</Button>
      </div>
      {data && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <Row title="Usados" a={data.used} />
          <Row title="Nuevos" a={data.new} />
        </div>
      )}
    </div>
  );
}

// Catálogo 100% administrable: crear/editar modelos, capacidades y colores sin código.
function CatalogModelsManager({ onChange }) {
  const empty = { generation: "", name: "", capacities: "", colors: [], battery_cost: "0", active: true };
  const [models, setModels] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const load = () => adminApi.get("/admin/catalog-models").then((r) => setModels(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ generation: String(m.generation), name: m.name, capacities: (m.capacities || []).join(", "), colors: (m.colors || []).map((c) => ({ ...c })), battery_cost: String(m.battery_cost ?? 0), active: m.active !== false }); setOpen(true); };
  const setColor = (i, k, v) => setForm((f) => ({ ...f, colors: f.colors.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const addColor = () => setForm((f) => ({ ...f, colors: [...f.colors, { name: "", hex: "#cccccc" }] }));
  const rmColor = (i) => setForm((f) => ({ ...f, colors: f.colors.filter((_, idx) => idx !== i) }));
  const save = async () => {
    const caps = form.capacities.split(",").map((s) => s.trim()).filter(Boolean);
    const colors = form.colors.map((c) => ({ name: (c.name || "").trim(), hex: c.hex || "#cccccc" })).filter((c) => c.name);
    const body = { generation: Number(form.generation), capacities: caps, colors, battery_cost: Number(form.battery_cost || 0), active: form.active };
    if (!editing) {
      body.name = form.name.trim();
      if (!body.name) { toast.error("El nombre del modelo es obligatorio"); return; }
      if (!form.generation) { toast.error("La generación es obligatoria"); return; }
    }
    setSaving(true);
    try {
      if (editing) await adminApi.put(`/admin/catalog-models/${editing.id}`, body);
      else await adminApi.post("/admin/catalog-models", body);
      toast.success(editing ? "Modelo actualizado" : "Modelo creado");
      setOpen(false); await load(); onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar el modelo"); }
    finally { setSaving(false); }
  };
  const toggleActive = async (m) => {
    try { await adminApi.put(`/admin/catalog-models/${m.id}`, { active: !(m.active !== false) }); await load(); onChange && onChange(); }
    catch { toast.error("No se pudo cambiar el estado"); }
  };
  return (
    <div className="mb-6 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="catalog-models-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <p className="font-semibold text-zinc-900 text-sm">Catálogo de modelos (fuente única)</p>
          <p className="text-xs text-zinc-500">Crea o edita modelos, capacidades y colores sin código. Generaciones libres (18, 19, 20…).</p>
        </div>
        <Button onClick={openNew} className="h-9 rounded-xl bg-zinc-900 hover:bg-zinc-800 shrink-0" data-testid="catalog-add-model"><Plus className="w-4 h-4 mr-1.5" /> Agregar modelo</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {models.map((m) => (
          <div key={m.id} className={`rounded-xl border p-2.5 w-[152px] ${m.active !== false ? "border-zinc-200" : "border-zinc-200 bg-zinc-50 opacity-70"}`} data-testid={`catalog-model-${m.id}`}>
            <p className="text-xs font-semibold text-zinc-900 leading-tight">{m.name}</p>
            <p className="text-[10px] text-zinc-500 mb-1.5">Gen {m.generation} · {(m.capacities || []).length} cap · {(m.colors || []).length} col</p>
            <div className="flex gap-1">
              <Button onClick={() => openEdit(m)} variant="outline" className="h-7 rounded-lg text-[10px] px-2 flex-1" data-testid={`catalog-edit-${m.id}`}>Editar</Button>
              <Button onClick={() => toggleActive(m)} variant="outline" className={`h-7 rounded-lg text-[10px] px-2 ${m.active !== false ? "text-emerald-600 border-emerald-200" : "text-zinc-400"}`} data-testid={`catalog-toggle-${m.id}`}>{m.active !== false ? "Activo" : "Inactivo"}</Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="catalog-model-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar ${editing.name}` : "Agregar modelo"}</DialogTitle>
            <DialogDescription>Las combinaciones nuevas quedan con precio PENDIENTE hasta que lo configures.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Generación</Label><Input type="number" value={form.generation} onChange={(e) => setForm({ ...form, generation: e.target.value })} placeholder="18" className="h-10 rounded-xl" data-testid="catalog-field-generation" /></div>
              <div className="col-span-2"><Label className="text-xs">Nombre del modelo</Label><Input value={form.name} disabled={!!editing} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="iPhone 18" className="h-10 rounded-xl" data-testid="catalog-field-name" /></div>
            </div>
            <div><Label className="text-xs">Capacidades (separadas por coma)</Label><Input value={form.capacities} onChange={(e) => setForm({ ...form, capacities: e.target.value })} placeholder="256 GB, 512 GB, 1 TB, 2 TB" className="h-10 rounded-xl" data-testid="catalog-field-capacities" /></div>
            <div><Label className="text-xs">Costo de batería (USD)</Label><Input type="number" value={form.battery_cost} onChange={(e) => setForm({ ...form, battery_cost: e.target.value })} className="h-10 rounded-xl w-32" data-testid="catalog-field-battery" /></div>
            <div>
              <div className="flex items-center justify-between"><Label className="text-xs">Colores</Label><Button type="button" variant="outline" onClick={addColor} className="h-7 rounded-lg text-[11px] px-2" data-testid="catalog-add-color"><Plus className="w-3 h-3 mr-1" />Color</Button></div>
              <div className="space-y-1.5 mt-1.5">
                {form.colors.map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`catalog-color-row-${i}`}>
                    <input type="color" value={c.hex || "#cccccc"} onChange={(e) => setColor(i, "hex", e.target.value)} className="h-9 w-10 rounded border border-zinc-200 shrink-0" />
                    <Input value={c.name} onChange={(e) => setColor(i, "name", e.target.value)} placeholder="Nombre del color" className="h-9 rounded-xl flex-1" />
                    <Button type="button" variant="ghost" onClick={() => rmColor(i)} className="h-9 w-9 p-0 text-rose-500 shrink-0"><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                {form.colors.length === 0 && <p className="text-[11px] text-zinc-400">Sin colores. Puedes agregarlos ahora o después.</p>}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="catalog-field-active" /> Activo (disponible para nuevas operaciones)</label>
            <Button onClick={save} disabled={saving} className="h-11 w-full rounded-xl bg-zinc-900 hover:bg-zinc-800" data-testid="catalog-save-model">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Guardar cambios" : "Crear modelo")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PricesTab({ quickActions = [], onNavigate }) {
  const [prices, setPrices] = useState(null);
  const [rules, setRules] = useState(null);
  const [drafts, setDrafts] = useState({});       // id -> {high, low} (strings)
  const [batDrafts, setBatDrafts] = useState({});  // model -> battery_cost (string)
  const [savingId, setSavingId] = useState(null);
  const [savingModel, setSavingModel] = useState(null);
  const [filterGen, setFilterGen] = useState("all");
  const [sortNewest, setSortNewest] = useState(true);
  const [bulkPct, setBulkPct] = useState("");
  const [bulkDir, setBulkDir] = useState("up");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [colorsByModel, setColorsByModel] = useState({});
  const [howOpen, setHowOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);

  const load = async () => {
    const [pr, rl] = await Promise.all([
      adminApi.get("/admin/prices"),
      adminApi.get("/admin/rules"),
    ]);
    setPrices(pr.data);
    setRules(rl.data);
    const d = {}, b = {};
    pr.data.forEach((p) => {
      d[p.id] = { high: p.market_price_high == null ? "" : String(p.market_price_high), low: p.market_price_low == null ? "" : String(p.market_price_low) };
      b[p.model] = String(p.battery_cost ?? 0);
    });
    setDrafts(d); setBatDrafts(b);
  };

  useEffect(() => { load(); }, []);
  const loadColors = () => adminApi.get("/config").then((r) => { const m = {}; (r.data.models || []).forEach((x) => { m[x.name] = x.colors || []; }); setColorsByModel(m); }).catch(() => {});
  useEffect(() => { loadColors(); }, []);

  const margin = Number(rules?.margin_target_pct ?? 20) / 100;
  const acc = Number(rules?.accessories_cost ?? 10);
  const cashDisc = Number(rules?.cash_discount ?? 40);

  const previewHigh = (high) => {
    const m = Number(high);
    if (!m || m <= 0) return null;
    return round2(m * (1 - margin) - acc);
  };
  const previewLow = (low, batteryCost) => {
    const m = Number(low);
    if (!m || m <= 0) return null;
    return round2(m * (1 - margin) - acc - Number(batteryCost || 0));
  };

  const setField = (id, field, value) => setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const applyBulk = async () => {
    const pct = Number(bulkPct);
    if (!pct || pct <= 0) { toast.error("Ingresa un porcentaje válido"); return; }
    setBulkBusy(true);
    try {
      const { data } = await adminApi.post("/admin/prices/bulk-adjust", { percent: pct, direction: bulkDir });
      toast.success(`Precios ${bulkDir === "up" ? "aumentados" : "reducidos"} ${pct}% · ${data.updated} filas actualizadas`);
      setBulkConfirm(false); setBulkPct(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aplicar el ajuste"); }
    finally { setBulkBusy(false); }
  };

  const saveMarket = async (p) => {
    setSavingId(p.id);
    try {
      const hi = drafts[p.id].high === "" ? null : Number(drafts[p.id].high);
      const lo = drafts[p.id].low === "" ? null : Number(drafts[p.id].low);
      const r = await adminApi.put(`/admin/prices/${p.id}`, { market_price_high: hi, market_price_low: lo });
      setPrices((prev) => prev.map((x) => (x.id === p.id ? { ...x, ...r.data } : x)));
      toast.success(`Precio de mercado actualizado · ${p.model} ${p.capacity}`);
    } catch { toast.error("No se pudo guardar el precio de mercado"); }
    finally { setSavingId(null); }
  };

  const saveBattery = async (model) => {
    setSavingModel(model);
    try {
      const cost = Number(batDrafts[model]);
      await adminApi.put(`/admin/battery-costs`, { model, cost });
      setPrices((prev) => prev.map((x) => (x.model === model ? { ...x, battery_cost: cost } : x)));
      toast.success(`Costo de batería actualizado · ${model}`);
    } catch { toast.error("No se pudo guardar el costo de batería"); }
    finally { setSavingModel(null); }
  };

  if (!prices || !rules) return <Loading />;

  const groups = [];
  prices.forEach((p) => {
    let g = groups.find((x) => x.model === p.model);
    if (!g) { g = { model: p.model, items: [] }; groups.push(g); }
    g.items.push(p);
  });
  const genOf = (model) => { const m = model.match(/iPhone\s+(\d+)/i); return m ? Number(m[1]) : 0; };
  groups.forEach((g) => { g.gen = genOf(g.model); });
  const gens = [...new Set(groups.map((g) => g.gen))].sort((a, b) => b - a);
  let visible = filterGen === "all" ? groups : groups.filter((g) => String(g.gen) === filterGen);
  visible = sortNewest ? [...visible].reverse() : visible;
  return (
    <div data-testid="prices-panel">
      <div className="mb-5">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Precios Trade-In</h1>
        <p className="text-sm text-zinc-500 mt-1">Administra valores de mercado, márgenes y costos.</p>
        <button type="button" onClick={() => setHowOpen((v) => !v)} data-testid="prices-how-toggle" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-zinc-900">
          <Info className="w-4 h-4" /> ¿Cómo se calcula?
        </button>
        {howOpen && (
          <div data-testid="prices-how-panel" className="mt-2 rounded-xl border border-zinc-200 bg-white p-4 text-sm text-zinc-600">
            Precio de mercado <b>alto</b> (batería ≥ {rules.battery_threshold}%) y <b>bajo</b> (&lt; {rules.battery_threshold}%) por modelo/capacidad, y costo de batería por modelo. Trade-In alto = <b>mercado alto × (1 − margen) − accesorios</b>; bajo = <b>mercado bajo × (1 − margen) − accesorios − batería</b>. Deja vacío = PENDIENTE. Aplica solo a <b>nuevas</b> cotizaciones.
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="prices-toolbar">
        <div className="flex flex-wrap gap-1.5">
          <button
            data-testid="filter-gen-all"
            onClick={() => setFilterGen("all")}
            className={`h-9 px-3.5 rounded-full text-sm font-medium transition-colors ${filterGen === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
          >Todos</button>
          {gens.map((g) => (
            <button
              key={g}
              data-testid={`filter-gen-${g}`}
              onClick={() => setFilterGen(String(g))}
              className={`h-9 px-3.5 rounded-full text-sm font-medium transition-colors ${filterGen === String(g) ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            >{g}</button>
          ))}
        </div>
        <div className="sm:ml-auto flex rounded-full bg-zinc-100 p-1">
          <button
            data-testid="sort-newest"
            onClick={() => setSortNewest(true)}
            className={`h-7 px-3 rounded-full text-xs font-medium transition-colors ${sortNewest ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"}`}
          >Más nuevos primero</button>
          <button
            data-testid="sort-oldest"
            onClick={() => setSortNewest(false)}
            className={`h-7 px-3 rounded-full text-xs font-medium transition-colors ${!sortNewest ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"}`}
          >Más antiguos primero</button>
        </div>
      </div>
      <div className="mb-6" data-testid="prices-tools">
        <button type="button" onClick={() => setToolsOpen((v) => !v)} data-testid="prices-tools-toggle" className="inline-flex h-10 items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
          <Wrench className="w-4 h-4" /> Herramientas
          <ChevronDown className={`w-4 h-4 transition-transform ${toolsOpen ? "rotate-180" : ""}`} />
        </button>
        {toolsOpen && (
          <div className="mt-3 rounded-2xl border border-zinc-200 bg-zinc-50 p-4 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="bulk-price-panel">
            <div className="flex-1">
              <p className="font-semibold text-zinc-900 text-sm">Ajuste masivo de precios</p>
              <p className="text-xs text-zinc-500 mt-0.5">Sube o baja <b>todos</b> los precios de mercado (alto y bajo) por un porcentaje. Las capacidades PENDIENTES no se modifican. Solo afecta nuevas cotizaciones.</p>
            </div>
            <div className="flex rounded-full bg-white border border-zinc-200 p-1 shrink-0">
              <button data-testid="bulk-dir-up" onClick={() => setBulkDir("up")} className={`h-8 px-3 rounded-full text-xs font-medium transition-colors ${bulkDir === "up" ? "bg-emerald-600 text-white" : "text-zinc-500"}`}>Subir</button>
              <button data-testid="bulk-dir-down" onClick={() => setBulkDir("down")} className={`h-8 px-3 rounded-full text-xs font-medium transition-colors ${bulkDir === "down" ? "bg-red-600 text-white" : "text-zinc-500"}`}>Bajar</button>
            </div>
            <div className="relative shrink-0">
              <Input data-testid="bulk-percent" type="number" min="0" max="90" placeholder="0" value={bulkPct} onChange={(e) => setBulkPct(e.target.value)} className="h-10 w-24 rounded-xl pr-7" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">%</span>
            </div>
            <Button data-testid="bulk-apply-open" onClick={() => { const pct = Number(bulkPct); if (!pct || pct <= 0) { toast.error("Ingresa un porcentaje válido"); return; } setBulkConfirm(true); }} className="h-10 rounded-xl bg-zinc-900 hover:bg-zinc-800 shrink-0">Aplicar a todos</Button>
          </div>
        )}
      </div>
      <ImageAuditPanel />
      <div className="space-y-6">
        {visible.map((group) => {
          const first = group.items[0];
          return (
            <div key={group.model} className="rounded-2xl border-2 border-zinc-200 bg-white p-5 shadow-sm" data-testid={`model-group-${slug(group.model)}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 pb-4 mb-4 border-b border-zinc-100">
                <div className="flex flex-col gap-3 min-w-0">
                  <h3 className="text-lg font-semibold text-zinc-900">{group.model}</h3>
                  <ModelImageManager model={group.model} />
                  <ModelColorImages model={group.model} colors={colorsByModel[group.model]} />
                </div>
                <div className="flex items-end gap-2 shrink-0">
                  <div>
                    <Label className="text-xs text-zinc-500">Costo batería ($)</Label>
                    <Input type="number" data-testid={`battery-cost-${slug(group.model)}`} value={batDrafts[group.model] ?? ""} onChange={(e) => setBatDrafts((prev) => ({ ...prev, [group.model]: e.target.value }))} className="mt-1 h-10 w-24 rounded-xl" />
                  </div>
                  <Button data-testid={`battery-cost-save-${slug(group.model)}`} onClick={() => saveBattery(group.model)} disabled={savingModel === group.model || Number(batDrafts[group.model]) === first.battery_cost} className="h-10 rounded-xl bg-zinc-900 hover:bg-zinc-800">
                    {savingModel === group.model ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {group.items.map((p) => {
                  const d = drafts[p.id] || { high: "", low: "" };
                  const pending = d.high === "" && d.low === "";
                  const pvH = previewHigh(d.high);
                  const pvL = previewLow(d.low, batDrafts[p.model]);
                  return (
                    <div key={p.id} className="rounded-xl bg-zinc-50 p-4 flex flex-col sm:flex-row sm:items-end gap-3" data-testid={`price-row-${p.id}`}>
                      <div className="flex-1">
                        <span className="text-xs text-zinc-400">Capacidad</span>
                        <p className="font-medium flex items-center gap-2">{p.capacity}
                          {pending && <span data-testid={`market-pending-${p.id}`} className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">PENDIENTE</span>}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Precio de mercado<br /><span className="text-[11px] font-medium text-zinc-400">Batería buena ≥{rules.battery_threshold}%</span></Label>
                        <Input type="number" placeholder="—" data-testid={`market-high-${p.id}`} value={d.high} onChange={(e) => setField(p.id, "high", e.target.value)} className="mt-1 h-11 w-full sm:w-28 rounded-xl" />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Precio de mercado<br /><span className="text-[11px] font-medium text-zinc-400">Batería degradada &lt;{rules.battery_threshold}%</span></Label>
                        <Input type="number" placeholder="—" data-testid={`market-low-${p.id}`} value={d.low} onChange={(e) => setField(p.id, "low", e.target.value)} className="mt-1 h-11 w-full sm:w-28 rounded-xl" />
                      </div>
                      <div className="w-full sm:w-48 rounded-xl border border-blue-200 bg-blue-50/60 px-3.5 py-2.5" data-testid={`trade-in-preview-${p.id}`}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Tag className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">Trade-In</span>
                        </div>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-[11px] font-medium text-blue-500">Batería buena ≥{rules.battery_threshold}%</span>
                          <span className="text-[22px] font-bold leading-none text-blue-700 tabular-nums">{pvH != null ? `$${pvH.toFixed(2)}` : "—"}</span>
                        </div>
                        <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-blue-100 pt-1">
                          <span className="text-[11px] font-medium text-zinc-400">Batería degradada &lt;{rules.battery_threshold}%</span>
                          <span className="text-[22px] font-bold leading-none text-zinc-500 tabular-nums">{pvL != null ? `$${pvL.toFixed(2)}` : "—"}</span>
                        </div>
                      </div>
                      <div className="w-full sm:w-48 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3.5 py-2.5" data-testid={`cash-preview-${p.id}`}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Wallet className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 leading-tight">Compra directa / Efectivo</span>
                        </div>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-[11px] font-medium text-emerald-500">Batería buena ≥{rules.battery_threshold}%</span>
                          <span className="text-[22px] font-bold leading-none text-emerald-700 tabular-nums">{pvH != null ? `$${(pvH - cashDisc).toFixed(2)}` : "—"}</span>
                        </div>
                        <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-emerald-100 pt-1">
                          <span className="text-[11px] font-medium text-zinc-400">Batería degradada &lt;{rules.battery_threshold}%</span>
                          <span className="text-[22px] font-bold leading-none text-zinc-500 tabular-nums">{pvL != null ? `$${(pvL - cashDisc).toFixed(2)}` : "—"}</span>
                        </div>
                      </div>
                      <Button
                        data-testid={`price-save-${p.id}`}
                        onClick={() => saveMarket(p)}
                        disabled={savingId === p.id}
                        className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800"
                      >
                        {savingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Guardar</>}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <Dialog open={bulkConfirm} onOpenChange={(o) => !o && setBulkConfirm(false)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle>¿Aplicar ajuste a todos los precios?</DialogTitle>
            <DialogDescription>
              Se {bulkDir === "up" ? "aumentarán" : "reducirán"} en <b>{bulkPct}%</b> todos los precios de mercado (alto y bajo) publicados. Las capacidades PENDIENTES no cambian. Afecta solo nuevas cotizaciones.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setBulkConfirm(false)} className="h-11 rounded-xl flex-1" data-testid="bulk-cancel">Cancelar</Button>
            <Button onClick={applyBulk} disabled={bulkBusy} className="h-11 rounded-xl flex-1 bg-zinc-900 hover:bg-zinc-800" data-testid="bulk-confirm">{bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sí, aplicar"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------- REGLAS ------------------------------- */
function RulesTab() {
  const [rules, setRules] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customMargin, setCustomMargin] = useState("");

  const load = () => adminApi.get("/admin/rules").then((r) => {
    setRules(r.data);
    setDraft({
      margin_target_pct: r.data.margin_target_pct,
      accessories_cost: r.data.accessories_cost,
      cash_discount: r.data.cash_discount,
      battery_threshold: r.data.battery_threshold,
      box_bonus: r.data.box_bonus,
      aesthetic_excellent: r.data.aesthetic_adjustments.excellent,
      aesthetic_good: r.data.aesthetic_adjustments.good,
    });
  });

  useEffect(() => { load(); }, []);

  const reset = () => {
    if (!rules) return;
    setDraft({
      margin_target_pct: rules.margin_target_pct,
      accessories_cost: rules.accessories_cost,
      cash_discount: rules.cash_discount,
      battery_threshold: rules.battery_threshold,
      box_bonus: rules.box_bonus,
      aesthetic_excellent: rules.aesthetic_adjustments.excellent,
      aesthetic_good: rules.aesthetic_adjustments.good,
    });
    toast("Cambios descartados");
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.put("/admin/rules", {
        margin_target_pct: Number(draft.margin_target_pct),
        accessories_cost: Number(draft.accessories_cost),
        cash_discount: Number(draft.cash_discount),
        battery_threshold: Number(draft.battery_threshold),
        box_bonus: Number(draft.box_bonus),
        aesthetic_excellent: Number(draft.aesthetic_excellent),
        aesthetic_good: Number(draft.aesthetic_good),
      });
      setRules(r.data);
      toast.success("Configuración guardada correctamente");
    } catch (e) {
      toast.error("No se pudo guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const quickMargin = async (pct) => {
    const val = Number(pct);
    if (!(val >= 0 && val < 100)) { toast.error("El margen debe ser ≥ 0 y < 100"); return; }
    setSaving(true);
    try {
      const r = await adminApi.put("/admin/rules", {
        margin_target_pct: val,
        accessories_cost: Number(rules.accessories_cost),
        cash_discount: Number(rules.cash_discount),
        battery_threshold: Number(rules.battery_threshold),
        box_bonus: Number(rules.box_bonus),
        aesthetic_excellent: Number(rules.aesthetic_adjustments.excellent),
        aesthetic_good: Number(rules.aesthetic_adjustments.good),
      });
      setRules(r.data);
      setDraft((d) => ({ ...d, margin_target_pct: r.data.margin_target_pct }));
      toast.success(`Margen activo: ${r.data.margin_target_pct}%`);
    } catch { toast.error("No se pudo aplicar el margen"); }
    finally { setSaving(false); }
  };

  const quickCashDiscount = async (val) => {
    const v = Number(val);
    if (v < 0) { toast.error("El descuento Cash no puede ser negativo"); return; }
    setSaving(true);
    try {
      const r = await adminApi.put("/admin/rules", {
        margin_target_pct: Number(rules.margin_target_pct),
        accessories_cost: Number(rules.accessories_cost),
        battery_threshold: Number(rules.battery_threshold),
        box_bonus: Number(rules.box_bonus),
        aesthetic_excellent: Number(rules.aesthetic_adjustments.excellent),
        aesthetic_good: Number(rules.aesthetic_adjustments.good),
        cash_discount: v,
      });
      setRules(r.data);
      setDraft((d) => ({ ...d, cash_discount: r.data.cash_discount }));
      toast.success(`Descuento Cash activo: $${r.data.cash_discount}`);
    } catch { toast.error("No se pudo aplicar el descuento Cash"); }
    finally { setSaving(false); }
  };

  if (!draft) return <Loading />;

  const field = (key, label, help) => (
    <div className="rounded-2xl border border-zinc-200 p-4">
      <Label className="text-sm font-medium text-zinc-800">{label}</Label>
      {help && <p className="text-xs text-zinc-400 mt-0.5 mb-2">{help}</p>}
      <Input type="number" data-testid={`rule-${key}`} value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} className="h-11 rounded-xl mt-1" />
    </div>
  );

  return (
    <div data-testid="rules-panel">
      <p className="text-sm text-zinc-500 mb-5">Configuración Trade-In. Aplica solo a nuevas cotizaciones; las históricas no cambian.</p>

      <div className="rounded-2xl border border-zinc-200 p-4 mb-6" data-testid="promo-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-zinc-900">Promoción Trade-In</h3>
          <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full" data-testid="promo-active-margin">
            Activo: {Number(rules?.margin_target_pct)}%
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[[20, "Normal"], [15, "Promo"], [12, "Promo"]].map(([pct, tag]) => {
            const active = Number(rules?.margin_target_pct) === pct;
            return (
              <button
                key={pct}
                data-testid={`promo-margin-${pct}`}
                onClick={() => quickMargin(pct)}
                disabled={saving}
                className={`h-14 rounded-xl border font-semibold transition-all flex flex-col items-center justify-center ${active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white hover:border-zinc-300 text-zinc-800"}`}
              >
                <span className="text-lg leading-none">{pct}%</span>
                <span className={`text-[11px] ${active ? "text-emerald-300" : "text-zinc-400"}`}>{tag}{active ? " · activo" : ""}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs text-zinc-500">Margen personalizado (%)</Label>
            <Input type="number" data-testid="promo-margin-custom-input" value={customMargin} onChange={(e) => setCustomMargin(e.target.value)} placeholder="Ej. 18" className="h-11 rounded-xl mt-1" />
          </div>
          <Button data-testid="promo-margin-custom-apply" onClick={() => quickMargin(customMargin)} disabled={saving || customMargin === ""} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800">
            Aplicar
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 p-4 mb-6" data-testid="cash-section">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-zinc-900">Venta directa en efectivo</h3>
          <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full" data-testid="cash-active-discount">Descuento: ${Number(rules?.cash_discount)}</span>
        </div>
        <p className="text-xs text-zinc-400 mb-3">Cash = Trade-In − descuento. Aplica solo a nuevas cotizaciones "Solo vender mi iPhone".</p>
        <div className="grid grid-cols-3 gap-2">
          {[30, 40, 50].map((v) => {
            const active = Number(rules?.cash_discount) === v;
            return (
              <button key={v} data-testid={`cash-discount-${v}`} onClick={() => quickCashDiscount(v)} disabled={saving}
                className={`h-12 rounded-xl border font-semibold transition-all ${active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white hover:border-zinc-300 text-zinc-800"}`}>
                ${v}{active ? " · activo" : ""}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {field("margin_target_pct", "Margen objetivo Trade-In (%)", "Ej. 20 normal, 15 o 12 en promoción. Debe ser ≥ 0 y < 100.")}
        {field("accessories_cost", "Costo de accesorios ($)", "Forro + vidrio templado. Se descuenta siempre.")}
        {field("battery_threshold", "Umbral de batería (%)", "≥ este valor no descuenta batería; por debajo sí.")}
        {field("box_bonus", "Bono por caja original ($)", "Se suma si el cliente tiene la caja.")}
        {field("aesthetic_excellent", "Ajuste condición Excelente ($)", "Normalmente 0.")}
        {field("aesthetic_good", "Ajuste condición Bueno ($)", "Descuento (valor negativo).")}
      </div>
      <p className="text-xs text-zinc-400 mt-4">Última modificación: {fmtDate(rules?.updated_at)}</p>
      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={reset} data-testid="rules-cancel" className="h-12 rounded-2xl border-zinc-200 flex-1"><X className="w-4 h-4 mr-1.5" /> Cancelar</Button>
        <Button onClick={save} disabled={saving} data-testid="rules-save" className="h-12 rounded-2xl bg-zinc-900 hover:bg-zinc-800 flex-1">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Guardar</>}
        </Button>
      </div>
    </div>
  );
}

/* ---------------------------- COTIZACIONES ---------------------------- */
function QuotesTab({ canManage }) {
  const [quotes, setQuotes] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [detail, setDetail] = useState(null);
  const [tiOpen, setTiOpen] = useState(false);
  const [tiList, setTiList] = useState([]);
  const [tiOutboundId, setTiOutboundId] = useState("");
  const [tiSalePrice, setTiSalePrice] = useState("");
  const [tiSaving, setTiSaving] = useState(false);
  const tiOut = tiList.find((it) => it.id === tiOutboundId);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    return adminApi.get("/admin/quotes", { params }).then((r) => setQuotes(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const patchStatus = async (val, extra = {}) => {
    await adminApi.patch(`/admin/quotes/${detail.id}/operation-status`, { operation_status: val, ...extra });
    setDetail({ ...detail, operation_status: val });
    toast.success("Estado de operación actualizado");
    load();
  };

  const onOpStatusChange = async (val) => {
    // Trade-In realizado exige seleccionar el equipo de inventario que SALE.
    if (val === "TRADE-IN REALIZADO" && detail.operation_status !== "TRADE-IN REALIZADO") {
      setTiOutboundId(""); setTiSalePrice(""); setTiOpen(true);
      try {
        const r = await adminApi.get("/admin/inventory", { params: { status: "DISPONIBLE" } });
        setTiList(r.data);
      } catch { toast.error("No se pudo cargar el inventario disponible"); }
      return;
    }
    try { await patchStatus(val); } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
  };

  const confirmTradeIn = async () => {
    if (!tiOutboundId) { toast.error("Selecciona el equipo que el cliente se llevará para completar el Trade-In."); return; }
    setTiSaving(true);
    try {
      await patchStatus("TRADE-IN REALIZADO", { outbound_inventory_id: tiOutboundId, outbound_sale_price: tiSalePrice ? Number(tiSalePrice) : null });
      setTiOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo completar el Trade-In"); }
    finally { setTiSaving(false); }
  };

  const statusPill = (s) =>
    s === "technical_evaluation"
      ? <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-full"><AlertTriangle className="w-3 h-3" /> Evaluación técnica</span>
      : <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full"><CheckCircle2 className="w-3 h-3" /> Cotización</span>;

  return (
    <div data-testid="quotes-panel">
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="quotes-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar por ID (ej. TI-260001)"
            className="h-11 rounded-xl pl-9"
          />
        </div>
        <Button onClick={load} data-testid="quotes-search-btn" className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800">Buscar</Button>
      </div>

      <div className="flex gap-2 mb-5">
        {[["all", "Todas"], ["estimated", "Cotización"], ["technical_evaluation", "Evaluación técnica"]].map(([key, label]) => (
          <button
            key={key}
            data-testid={`filter-${key}`}
            onClick={() => setStatus(key)}
            className={`px-4 h-9 rounded-full text-sm font-medium transition-all ${status === key ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {!quotes ? <Loading /> : quotes.length === 0 ? (
        <p className="text-sm text-zinc-400 py-10 text-center">No hay cotizaciones que coincidan.</p>
      ) : (
        <div className="space-y-2.5">
          {quotes.map((qt) => (
            <button
              key={qt.id}
              data-testid={`quote-row-${qt.id}`}
              onClick={() => setDetail(qt)}
              className="w-full text-left rounded-2xl border border-zinc-200 p-4 hover:border-zinc-300 hover:shadow-[0_2px_12px_rgba(0,0,0,0.05)] transition-all"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-semibold">{qt.id}</span>
                {statusPill(qt.status)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600">{qt.model} · {qt.capacity} · {qt.battery_percent}%</span>
                <span className="font-semibold">{qt.price != null ? `$${qt.price}` : "—"}</span>
              </div>
              <span className="text-xs text-zinc-400">{fmtDate(qt.created_at)}</span>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="quote-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{detail.id}</span>
                  {statusPill(detail.status)}
                </DialogTitle>
                <DialogDescription className="sr-only">Detalle completo de la cotización {detail.id}</DialogDescription>
              </DialogHeader>
              <div className="mt-2">
                <Row label="Fecha y hora" value={fmtDate(detail.created_at)} />
                <Row label="Modelo" value={detail.model} />
                <Row label="Capacidad" value={detail.capacity} />
                <Row label="Batería" value={`${detail.battery_percent}% (${detail.battery_level === "high" ? "≥" : "<"} ${detail.battery_threshold}%)`} />
                {detail.charge_cycles != null && <Row label="Ciclos de carga" value={String(detail.charge_cycles)} />}
                <Row label="Condición" value={detail.aesthetic_label || "—"} />
                {detail.sim_type && <Row label="Tipo de SIM" value={detail.sim_type} />}
                <Row label="Caja original" value={detail.has_box ? `Sí (+$${detail.box_bonus})` : "No"} />
                <Row label="Daño" value={detail.requires_evaluation ? detail.problem_label : "Ninguno"} />
                {!detail.requires_evaluation && detail.market_price_used != null && <Row label="Precio mercado" value={`$${Number(detail.market_price_used).toFixed(2)}`} />}
                {!detail.requires_evaluation && detail.margin_used != null && <Row label="Margen usado" value={`${detail.margin_used}%`} />}
                {!detail.requires_evaluation && detail.battery_cost_used != null && <Row label="Costo batería" value={`$${Number(detail.battery_cost_used).toFixed(2)}${detail.battery_level === "low" ? " (descontado)" : " (no aplica)"}`} />}
                {!detail.requires_evaluation && <Row label="Precio base" value={detail.base_price != null ? `$${Number(detail.base_price).toFixed(2)}` : "—"} />}
                {!detail.requires_evaluation && <Row label="Ajuste estético" value={`$${detail.aesthetic_adjustment}`} />}
                {!detail.requires_evaluation && detail.charge_cycles_adjustment ? <Row label="Ajuste por ciclos" value={`$${detail.charge_cycles_adjustment} (> 400 ciclos)`} /> : null}
                <Row label="Precio estimado" value={detail.price != null ? `$${Number(detail.price).toFixed(2)}` : "Requiere evaluación"} strong />
                {detail.price != null && <Row label="CASH (referencia interna)" value={`$${(Number(detail.price) - 40).toFixed(2)}`} strong />}
                <Row label="Estado" value={detail.requires_evaluation ? "Evaluación técnica" : "Cotización normal"} />
                <div className="pt-3 mt-1 border-t border-zinc-100">
                  <label className="text-xs text-zinc-500">Estado de operación</label>
                  <select
                    data-testid="op-status-select"
                    disabled={!canManage}
                    value={detail.operation_status || "PENDIENTE"}
                    onChange={(e) => onOpStatusChange(e.target.value)}
                    className="mt-1 w-full h-10 rounded-xl border border-zinc-200 px-3 text-sm disabled:bg-zinc-50 disabled:text-zinc-400"
                  >
                    {["PENDIENTE", "TRADE-IN REALIZADO", "CASH REALIZADO", "NO REALIZADO"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {["TRADE-IN REALIZADO", "CASH REALIZADO"].includes(detail.operation_status) && (
                  <div className="pt-3 mt-1 border-t border-zinc-100">
                    <p className="text-xs text-zinc-500 mb-2">Operación concretada — genera el respaldo documental:</p>
                    <ContractButton operationType="trade_in" operation={detail} size="full" />
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={tiOpen} onOpenChange={(o) => { if (!o) setTiOpen(false); }}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="tradein-complete-dialog">
          <DialogHeader>
            <DialogTitle>Completar Trade-In</DialogTitle>
            <DialogDescription className="sr-only">Selecciona el equipo que el cliente se lleva del inventario</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            {detail && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3" data-testid="tradein-incoming">
                <p className="text-xs font-semibold text-emerald-700 mb-1">📥 EQUIPO QUE ENTRA</p>
                <p className="text-sm text-zinc-800">{detail.model} · {detail.capacity}{detail.color ? ` · ${detail.color}` : ""}</p>
                <p className="text-xs text-zinc-500">IMEI {detail.imei || "—"} · Precio Trade-In: {detail.price != null ? `$${Number(detail.price).toFixed(2)}` : "—"}</p>
              </div>
            )}
            <div>
              <label className="text-xs text-zinc-500">📤 Equipo que sale (solo DISPONIBLES)</label>
              <select data-testid="tradein-outbound-select" value={tiOutboundId} onChange={(e) => setTiOutboundId(e.target.value)} className="mt-1 w-full h-10 rounded-xl border border-zinc-200 px-3 text-sm">
                <option value="">— Selecciona un equipo —</option>
                {tiList.map((it) => <option key={it.id} value={it.id}>{`${it.id} · ${it.model} ${it.capacity || ""}${it.color ? " " + it.color : ""}${it.imei ? " · " + it.imei : ""}`}</option>)}
              </select>
              {tiList.length === 0 && <p className="text-xs text-amber-600 mt-1">No hay equipos DISPONIBLES en inventario. Agrega mercancía primero.</p>}
            </div>
            {tiOut && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-3" data-testid="tradein-outgoing">
                <p className="text-xs font-semibold text-red-700 mb-1">📤 EQUIPO QUE SALE</p>
                <p className="text-sm text-zinc-800">{tiOut.model} · {tiOut.capacity || "—"}{tiOut.color ? ` · ${tiOut.color}` : ""}</p>
                <p className="text-xs text-zinc-500">{tiOut.imei ? `IMEI ${tiOut.imei} · ` : ""}{tiOut.sim_type ? `📶 ${tiOut.sim_type} · ` : ""}Costo: {tiOut.acquisition_cost != null ? `$${Number(tiOut.acquisition_cost).toFixed(0)}` : "—"}</p>
              </div>
            )}
            <div>
              <label className="text-xs text-zinc-500">Precio de venta del equipo que sale (opcional)</label>
              <Input data-testid="tradein-sale-price" type="number" value={tiSalePrice} onChange={(e) => setTiSalePrice(e.target.value)} placeholder="$" className="mt-1 h-10 rounded-xl" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1 h-11 rounded-xl" onClick={() => setTiOpen(false)} data-testid="tradein-cancel-btn">Cancelar</Button>
              <Button className="flex-1 h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800" disabled={tiSaving || !tiOutboundId} onClick={confirmTradeIn} data-testid="tradein-confirm-btn">{tiSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar Trade-In"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PERM_GROUPS = [
  { cat: "PRECIOS", items: [["prices:view", "Ver precios"], ["prices:edit", "Modificar precios"]] },
  { cat: "REGLAS", items: [["rules:view", "Ver reglas"], ["rules:edit", "Modificar reglas"]] },
  { cat: "INVENTARIO USADO", items: [["inventory:view", "Ver inventario"], ["inventory:manage", "Gestionar inventario"]] },
  { cat: "IPHONE NUEVOS", items: [["new_inventory:view", "Ver iPhone Nuevos"], ["new_inventory:manage", "Gestionar iPhone Nuevos"]] },
  { cat: "ACCESORIOS", items: [["accessories:view", "Ver accesorios"], ["accessories:manage", "Gestionar accesorios"]] },
  { cat: "RETIROS", items: [["withdrawals:view", "Ver retiros"], ["withdrawals:confirm", "Confirmar entrega de retiros"]] },
  { cat: "PAGOS / CUENTA CORRIENTE", items: [["cash_withdrawals:view", "Ver pagos y saldo de consignadores"], ["cash_withdrawals:confirm", "Confirmar/registrar pagos"]] },
  { cat: "RECEPCIÓN", items: [["shipments:receive", "Recibir preavisos de mercancía"]] },
  { cat: "COMPRAS", items: [["purchases:view", "Ver compras"], ["purchases:manage", "Gestionar compras"]] },
  { cat: "COTIZACIONES", items: [["quotes:view", "Ver cotizaciones"], ["quotes:manage", "Gestionar cotizaciones"]] },
  { cat: "VENTAS", items: [["sales:manage", "Gestionar ventas"]] },
  { cat: "INFORMES", items: [["reports:view", "Ver informes"]] },
  { cat: "IMÁGENES", items: [["images:manage", "Gestionar imágenes"]] },
  { cat: "DUEÑOS / CONSIGNADORES", items: [["owners:manage", "Gestionar dueños"]] },
  { cat: "USUARIOS", items: [["users:manage", "Gestionar usuarios"]] },
  { cat: "CONTRATOS", items: [["contract:config", "Configurar contratos"]] },
];

function PermissionsModal({ user, onClose, onSave }) {
  const extra = user?.extra_permissions || [];
  const effective = user?.permissions || [];
  const baseSet = new Set(effective.filter((p) => !extra.includes(p)));
  const [draft, setDraft] = useState(new Set(extra));
  const toggle = (p) => setDraft((prev) => { const n = new Set(prev); n.has(p) ? n.delete(p) : n.add(p); return n; });
  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="perm-modal">
        <DialogHeader>
          <DialogTitle>Gestionar permisos</DialogTitle>
          <DialogDescription>{user?.name} · {user?.email} · <span className="font-medium">{user?.role}</span></DialogDescription>
        </DialogHeader>
        <p className="text-xs text-zinc-500">Los permisos <b>incluidos por el rol</b> no se pueden quitar. Activa o desactiva <b>permisos adicionales</b>.</p>
        <div className="space-y-4 mt-1">
          {PERM_GROUPS.map((g) => (
            <div key={g.cat}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">{g.cat}</p>
              <div className="space-y-1.5">
                {g.items.map(([key, label]) => {
                  const inRole = baseSet.has(key);
                  const on = inRole || draft.has(key);
                  return (
                    <div key={key} className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-zinc-900">{label}</p>
                        <p className="text-[11px] text-zinc-400">{key}{inRole ? " · incluido por rol" : ""}</p>
                      </div>
                      <button type="button" disabled={inRole} data-testid={`perm-switch-${key}`} aria-pressed={on}
                        onClick={() => toggle(key)}
                        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-zinc-200"} ${inRole ? "opacity-50 cursor-not-allowed" : ""}`}>
                        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="h-11 rounded-xl flex-1">Cancelar</Button>
          <Button data-testid="perm-save" onClick={() => onSave([...draft])} className="h-11 rounded-xl flex-1 bg-zinc-900 hover:bg-zinc-800">Guardar permisos</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [allPerms, setAllPerms] = useState([]);
  const [owners, setOwners] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "vendedor", owner_id: "", investor_id: "" });
  const [saving, setSaving] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const load = () => {
    adminApi.get("/admin/users").then((r) => setUsers(r.data));
    adminApi.get("/admin/permissions").then((r) => setAllPerms(r.data.permissions)).catch(() => {});
    adminApi.get("/admin/owners").then((r) => setOwners(r.data)).catch(() => {});
    adminApi.get("/admin/investors").then((r) => setInvestors(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    setSaving(true);
    try {
      await adminApi.post("/admin/users", form);
      toast.success("Usuario creado");
      setForm({ email: "", name: "", password: "", role: "vendedor", owner_id: "", investor_id: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const upd = async (email, patch) => {
    try { await adminApi.put(`/admin/users/${encodeURIComponent(email)}`, patch); toast.success("Usuario actualizado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const ownerLabel = (id) => { const o = owners.find((x) => x.id === id); return o ? `${o.name} (${o.code})` : null; };
  const investorLabel = (id) => { const o = investors.find((x) => x.id === id); return o ? o.name : null; };
  const changePw = async (email) => {
    const pw = window.prompt(`Nueva contraseña para ${email}:`);
    if (pw) upd(email, { password: pw });
  };
  return (
    <div data-testid="users-panel">
      <div className="rounded-2xl border border-zinc-200 p-5 mb-6">
        <h3 className="text-sm font-semibold text-zinc-900 mb-3">Crear usuario (vendedor, manager, consignador o inversionista)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input data-testid="user-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl" />
          <Input data-testid="user-name" placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" />
          <Input data-testid="user-password" placeholder="Contraseña" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl" />
          <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-11 rounded-xl border border-zinc-200 px-3 text-sm">
            <option value="vendedor">Vendedor</option>
            <option value="manager">Manager</option>
            <option value="consignador">Consignador (portal)</option>
            <option value="inversionista">Inversionista (portal)</option>
          </select>
          {form.role === "consignador" && (
            <select data-testid="user-owner" value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })} className="h-11 rounded-xl border border-zinc-200 px-3 text-sm sm:col-span-2">
              <option value="">Asociar a dueño/consignador…</option>
              {owners.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.code})</option>)}
            </select>
          )}
          {form.role === "inversionista" && (
            <select data-testid="user-investor" value={form.investor_id} onChange={(e) => setForm({ ...form, investor_id: e.target.value })} className="h-11 rounded-xl border border-zinc-200 px-3 text-sm sm:col-span-2">
              <option value="">Asociar a inversionista…</option>
              {investors.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
        </div>
        <Button data-testid="user-create-btn" onClick={create} disabled={saving || !form.email || !form.password || !form.name} className="mt-3 h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear usuario"}
        </Button>
      </div>
      {!users ? <Loading /> : (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.email} data-testid={`user-row-${u.email}`} className="rounded-2xl border border-zinc-200 p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-medium text-zinc-900">{u.name} <span className="text-xs font-semibold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded ml-1">{u.role}</span></p>
                  <p className="text-xs text-zinc-400">{u.email}</p>
                </div>
                {u.role === "admin" ? (
                  <span className="text-xs text-emerald-600 font-medium">Acceso total</span>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" data-testid={`user-edit-${u.email}`} onClick={() => setEditUser(u)} className="h-9 rounded-lg text-xs">Editar</Button>
                    <Button variant="outline" data-testid={`user-toggle-${u.email}`} onClick={() => upd(u.email, { active: !u.active })} className="h-9 rounded-lg text-xs">{u.active ? "Desactivar" : "Activar"}</Button>
                    <Button variant="outline" data-testid={`user-pw-${u.email}`} onClick={() => changePw(u.email)} className="h-9 rounded-lg text-xs">Cambiar clave</Button>
                    {!u.active && <span className="text-xs text-red-500">Inactivo</span>}
                  </div>
                )}
              </div>
              {u.role === "manager" && (
                <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between gap-3">
                  <p className="text-xs text-zinc-500">{(u.extra_permissions || []).length} permiso(s) adicional(es)</p>
                  <Button variant="outline" data-testid={`perm-manage-${u.email}`} onClick={() => setPermUser(u)} className="h-9 rounded-lg text-xs">Gestionar permisos</Button>
                </div>
              )}
              {u.role === "consignador" && (
                <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-zinc-500" data-testid={`user-owner-label-${u.email}`}>
                    Dueño asociado: <span className="font-medium text-zinc-700">{ownerLabel(u.owner_id) || "— sin asignar —"}</span>
                  </p>
                  <select data-testid={`user-owner-edit-${u.email}`} value={u.owner_id || ""} onChange={(e) => { const v = e.target.value; if (v) upd(u.email, { owner_id: v }); }} className="h-9 rounded-lg border border-zinc-200 px-2 text-xs">
                    <option value="">Cambiar dueño…</option>
                    {owners.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.code})</option>)}
                  </select>
                </div>
              )}
              {u.role === "inversionista" && (
                <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-zinc-500" data-testid={`user-investor-label-${u.email}`}>
                    Inversionista asociado: <span className="font-medium text-zinc-700">{investorLabel(u.investor_id) || "— sin asignar —"}</span>
                  </p>
                  <select data-testid={`user-investor-edit-${u.email}`} value={u.investor_id || ""} onChange={(e) => { const v = e.target.value; if (v) upd(u.email, { investor_id: v }); }} className="h-9 rounded-lg border border-zinc-200 px-2 text-xs">
                    <option value="">Cambiar inversionista…</option>
                    {investors.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {permUser && <PermissionsModal user={permUser} onClose={() => setPermUser(null)} onSave={async (perms) => { await upd(permUser.email, { extra_permissions: perms }); setPermUser(null); }} />}
      {editUser && <EditUserModal user={editUser} owners={owners} investors={investors} onClose={() => setEditUser(null)} onSave={async (patch) => { await upd(editUser.email, patch); setEditUser(null); }} />}
    </div>
  );
}

function EditUserModal({ user, owners, investors, onClose, onSave }) {
  const [f, setF] = useState({ name: user.name || "", email: user.email || "", role: user.role, owner_id: user.owner_id || "", investor_id: user.investor_id || "" });
  const [saving, setSaving] = useState(false);
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim());
  const valid = f.name.trim() && emailOk && (f.role !== "consignador" || f.owner_id) && (f.role !== "inversionista" || f.investor_id);
  const save = async () => {
    setSaving(true);
    try { await onSave({ name: f.name.trim(), email: f.email.trim().toLowerCase(), role: f.role, owner_id: f.role === "consignador" ? f.owner_id : null, investor_id: f.role === "inversionista" ? f.investor_id : null }); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="user-edit-dialog">
        <DialogHeader><DialogTitle>Editar usuario</DialogTitle>
          <DialogDescription>Modifica nombre, email y rol. La contraseña se cambia por separado.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <label className="flex flex-col gap-0.5"><span className="text-[11px] text-zinc-500 px-1">Nombre</span>
            <Input data-testid="user-edit-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11 rounded-xl" /></label>
          <label className="flex flex-col gap-0.5"><span className="text-[11px] text-zinc-500 px-1">Email (se usa para iniciar sesión)</span>
            <Input data-testid="user-edit-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11 rounded-xl" />
            {!emailOk && f.email && <span className="text-[11px] text-rose-500 px-1">Email inválido</span>}</label>
          <label className="flex flex-col gap-0.5"><span className="text-[11px] text-zinc-500 px-1">Rol</span>
            <select data-testid="user-edit-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="h-11 rounded-xl border border-zinc-200 px-3 text-sm">
              <option value="vendedor">Vendedor</option>
              <option value="manager">Manager</option>
              <option value="consignador">Consignador (portal)</option>
              <option value="inversionista">Inversionista (portal)</option>
            </select></label>
          {f.role === "consignador" && (
            <label className="flex flex-col gap-0.5"><span className="text-[11px] text-zinc-500 px-1">Dueño asociado *</span>
              <select data-testid="user-edit-owner" value={f.owner_id} onChange={(e) => setF({ ...f, owner_id: e.target.value })} className="h-11 rounded-xl border border-zinc-200 px-3 text-sm">
                <option value="">Asociar a dueño…</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.code})</option>)}
              </select></label>
          )}
          {f.role === "inversionista" && (
            <label className="flex flex-col gap-0.5"><span className="text-[11px] text-zinc-500 px-1">Inversionista asociado *</span>
              <select data-testid="user-edit-investor" value={f.investor_id} onChange={(e) => setF({ ...f, investor_id: e.target.value })} className="h-11 rounded-xl border border-zinc-200 px-3 text-sm">
                <option value="">Asociar a inversionista…</option>
                {(investors || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select></label>
          )}
        </div>
        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={onClose} className="h-11 rounded-xl flex-1">Cancelar</Button>
          <Button data-testid="user-edit-save" onClick={save} disabled={saving || !valid} className="h-11 rounded-xl flex-1 bg-zinc-900 hover:bg-zinc-800">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar cambios"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportsTab() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === "admin";
  const [period, setPeriod] = useState("this_month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [savingStart, setSavingStart] = useState(false);
  const load = (p = period) => {
    const params = { period: p };
    if (p === "custom") { params.date_from = from; params.date_to = to; }
    adminApi.get("/admin/reports", { params }).then((r) => setData(r.data));
  };
  const loadStats = () => adminApi.get("/admin/stats-config").then((r) => {
    setStats(r.data);
    setStartDate((r.data.official_start || "").slice(0, 10));
  });
  useEffect(() => { loadStats(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (period !== "custom") load(period); /* eslint-disable-next-line */ }, [period]);
  const saveStart = async (iso) => {
    setSavingStart(true);
    try {
      const official_start = iso || `${startDate}T00:00:00`;
      const { data: d } = await adminApi.put("/admin/stats-config", { official_start });
      setStats(d); setStartDate((d.official_start || "").slice(0, 10));
      toast.success("Inicio oficial de estadísticas actualizado");
      load(period);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSavingStart(false); }
  };
  const fmt = (iso) => { if (!iso) return iso; try { const hasTime = typeof iso === "string" && iso.length > 10; const d = new Date(hasTime ? iso : iso + "T00:00:00"); const b = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; return hasTime ? `${b} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : b; } catch { return iso; } };
  const Card = ({ label, value }) => (
    <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className="text-2xl font-bold text-zinc-900 mt-1 tabular-nums">{value}</p>
    </div>
  );
  return (
    <div data-testid="reports-panel">
      {stats && (
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 mb-5" data-testid="stats-start-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-zinc-400">Inicio oficial de estadísticas</p>
              <p className="text-sm font-semibold text-zinc-900 mt-0.5" data-testid="stats-start-value">{fmt(stats.official_start)}</p>
              <p className="text-[11px] text-zinc-500 mt-1">Los informes solo cuentan registros creados desde esta fecha. El historial anterior se conserva pero queda excluido de los KPIs.</p>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <Label className="text-xs text-zinc-500">Fecha de inicio</Label>
                  <Input type="date" data-testid="stats-start-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-10 rounded-xl mt-1" />
                </div>
                <Button data-testid="stats-start-save" onClick={() => saveStart()} disabled={savingStart || !startDate} className="h-10 rounded-xl bg-zinc-900 hover:bg-zinc-800">Guardar</Button>
                <Button data-testid="stats-start-now" variant="outline" onClick={() => saveStart(new Date().toISOString())} disabled={savingStart} className="h-10 rounded-xl">Comenzar desde ahora</Button>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-5">
        {[["this_month", "Este mes"], ["last_month", "Mes anterior"], ["last_30_days", "Últimos 30 días"], ["all", "Todo (oficial)"], ["custom", "Personalizado"]].map(([k, l]) => (
          <button key={k} data-testid={`report-period-${k}`} onClick={() => setPeriod(k)} className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${period === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{l}</button>
        ))}
      </div>
      {period === "custom" && (
        <div className="flex flex-wrap items-end gap-2 mb-5">
          <div><Label className="text-xs text-zinc-500">Desde</Label><Input type="date" data-testid="report-from" value={from} onChange={(e) => setFrom(e.target.value)} className="h-11 rounded-xl mt-1" /></div>
          <div><Label className="text-xs text-zinc-500">Hasta</Label><Input type="date" data-testid="report-to" value={to} onChange={(e) => setTo(e.target.value)} className="h-11 rounded-xl mt-1" /></div>
          <Button data-testid="report-apply" onClick={() => load("custom")} disabled={!from} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800">Aplicar</Button>
        </div>
      )}
      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6" data-testid="report-metrics">
            <Card label="Total cotizaciones" value={data.total} />
            <Card label="Trade-In realizados" value={data.trade_in_done} />
            <Card label="Cash realizados" value={data.cash_done} />
            <Card label="No realizadas" value={data.not_done} />
            <Card label="Pendientes" value={data.pending} />
            <Card label="Conversión" value={`${data.conversion_rate}%`} />
          </div>
          <h3 className="text-sm font-semibold text-zinc-900 mb-3">Modelos más cotizados</h3>
          <div className="space-y-2" data-testid="report-top-models">
            {data.top_models.length === 0 ? <p className="text-sm text-zinc-400">Sin datos en el período.</p> : data.top_models.map((m, i) => (
              <div key={`${m.model}-${m.capacity}`} className="flex items-center justify-between rounded-xl border border-zinc-200 px-4 py-2.5">
                <span className="text-sm text-zinc-700"><b className="text-zinc-400 mr-2">{i + 1}.</b>{m.model} {m.capacity}</span>
                <span className="text-sm font-semibold text-zinc-900">{m.count} cotizaciones</span>
              </div>
            ))}
          </div>
          {data.direct_purchases && (
            <div className="mt-8" data-testid="report-direct-purchases">
              <h3 className="text-sm font-semibold text-zinc-900 mb-3">Compras directas (equipos reales que entraron)</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Card label="Compras directas" value={data.direct_purchases.total} />
                <Card label="Equipos comprados" value={data.direct_purchases.units} />
                <Card label="Total pagado" value={`$${data.direct_purchases.total_paid}`} />
                <Card label="Promedio por equipo" value={`$${data.direct_purchases.avg_paid}`} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-zinc-500 mb-2">Por modelo</p>
                  {data.direct_purchases.by_model.length === 0 ? <p className="text-sm text-zinc-400">Sin datos.</p> : data.direct_purchases.by_model.map((m) => (
                    <div key={`${m.model}-${m.capacity}`} className="flex justify-between rounded-xl border border-zinc-200 px-3 py-2 mb-1.5 text-sm"><span>{m.model} {m.capacity}</span><span className="font-semibold">{m.count}</span></div>
                  ))}
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-2">Por vendedor</p>
                  {data.direct_purchases.by_vendor.length === 0 ? <p className="text-sm text-zinc-400">Sin datos.</p> : data.direct_purchases.by_vendor.map((v) => (
                    <div key={v.vendor} className="flex justify-between rounded-xl border border-zinc-200 px-3 py-2 mb-1.5 text-sm"><span>{v.vendor}</span><span className="font-semibold">{v.count}</span></div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PurchasesTab({ canManage }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => { adminApi.get("/admin/direct-purchases", { params: q ? { q } : {} }).then((r) => setList(r.data)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const cancel = async (id) => {
    const reason = window.prompt("Motivo de anulación (obligatorio):");
    if (reason == null) return;
    if (!reason.trim()) { toast.error("El motivo es obligatorio"); return; }
    try { await adminApi.patch(`/admin/direct-purchases/${id}/cancel`, { reason: reason.trim() }); toast.success("Compra anulada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo anular"); }
  };

  return (
    <div data-testid="purchases-panel">
      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="purchase-search" placeholder="Buscar por cliente, IMEI, modelo, fecha o vendedor" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="h-11 rounded-xl pl-9" />
        </div>
        <Button data-testid="purchase-search-btn" variant="outline" onClick={load} className="h-11 rounded-xl">Buscar</Button>
        {canManage && <Button data-testid="purchase-new-btn" onClick={() => setOpen(true)} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800">+ Registrar compra</Button>}
      </div>

      {!list ? <Loading /> : list.length === 0 ? <p className="text-sm text-zinc-400">Sin compras directas.</p> : (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs"><tr>
              {["Fecha", "Cliente", "Modelo", "Cap.", "IMEI", "Pagado", "Pago", "Vendedor", "Estado", "Inventario", "Contrato", ""].map((h) => <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} data-testid={`purchase-row-${p.id}`} className="border-t border-zinc-100">
                  <td className="px-3 py-2 whitespace-nowrap">{(p.purchase_date || p.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2">{p.customer?.name || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{p.model}</td>
                  <td className="px-3 py-2">{p.capacity || "—"}</td>
                  <td className="px-3 py-2">{p.imei || "—"}</td>
                  <td className="px-3 py-2 font-semibold">${Number(p.price_paid).toFixed(0)}</td>
                  <td className="px-3 py-2">{p.payment_method || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{p.created_by}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${p.status === "ANULADA" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>{p.status}</span></td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-500" data-testid={`purchase-inventory-${p.id}`}>{p.inventory_id || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-500" data-testid={`purchase-contract-${p.id}`}>{p.contract_number || "—"}</td>
                  <td className="px-3 py-2"><div className="flex items-center gap-3 whitespace-nowrap">
                    {p.status !== "ANULADA" && <ContractButton operationType="purchase" operation={p} />}
                    {canManage && p.status !== "ANULADA" && <button data-testid={`purchase-edit-${p.id}`} onClick={() => setEditing(p)} className="text-xs text-indigo-600 hover:underline">Editar</button>}
                    {canManage && p.status !== "ANULADA" && <button data-testid={`purchase-cancel-${p.id}`} onClick={() => cancel(p.id)} className="text-xs text-red-500 hover:underline">Anular</button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DirectPurchaseDialog open={open || !!editing} editing={editing} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }} onCreated={() => { setEditing(null); load(); }} />
    </div>
  );
}

function ContractConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi.get("/admin/contract-config").then((r) => setCfg(r.data)).catch(() => toast.error("No se pudo cargar la configuración")); }, []);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const onLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("El logo no debe superar 2MB"); return; }
    const r = new FileReader(); r.onload = () => set("logo_base64", r.result); r.readAsDataURL(f);
  };
  const save = async () => {
    setSaving(true);
    try { const { data } = await adminApi.put("/admin/contract-config", cfg); setCfg(data); toast.success("Datos del contrato guardados"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); } finally { setSaving(false); }
  };
  if (!cfg) return <Loading />;
  const field = (k, label, ph) => (
    <div key={k}>
      <Label className="text-xs text-zinc-500">{label}</Label>
      <Input data-testid={`contract-cfg-${k}`} value={cfg[k] || ""} placeholder={ph} onChange={(e) => set(k, e.target.value)} className="h-11 rounded-xl mt-1" />
    </div>
  );
  return (
    <div data-testid="contract-config-panel" className="space-y-5">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex gap-2">
        <Building2 className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Estos datos se usan en los contratos que se generen <strong>a partir de ahora</strong>. Los contratos ya generados conservan sus datos originales (respaldo histórico).</span>
      </div>
      <div className="rounded-2xl border border-zinc-200 p-5">
        <h3 className="text-sm font-semibold mb-3">Datos legales del comprador (PUNTO PLAY)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {field("buyer_name", "Nombre comercial", "PUNTO PLAY")}
          {field("tagline", "Lema / subtítulo", "GAMER STORE")}
          {field("razon_social", "Razón social (opcional)", "Punto Play C.A.")}
          {field("doc_rif", "Documento / RIF (opcional)", "J-40123456-7")}
          {field("phone", "Teléfono", "+58 ...")}
          <div className="sm:col-span-2">{field("address", "Dirección", "Av. ..., Local ...")}</div>
        </div>
        <div className="mt-4">
          <Label className="text-xs text-zinc-500">Logo</Label>
          <div className="flex items-center gap-4 mt-1">
            {cfg.logo_base64 ? <img src={cfg.logo_base64} alt="logo" className="h-14 w-auto rounded-lg border border-zinc-200 bg-white object-contain" /> : <span className="text-xs text-zinc-400">Sin logo</span>}
            <input data-testid="contract-cfg-logo" type="file" accept="image/*" onChange={onLogo} className="text-sm" />
            {cfg.logo_base64 && <button onClick={() => set("logo_base64", "")} className="text-xs text-red-500 hover:underline">Quitar</button>}
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-zinc-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold">Textos del contrato</h3>
        <div>
          <Label className="text-xs text-zinc-500">Texto legal principal (usa {"{buyer}"} y {"{seller}"})</Label>
          <textarea data-testid="contract-cfg-intro_text" value={cfg.intro_text || ""} onChange={(e) => set("intro_text", e.target.value)} rows={3} className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Texto del pie del contrato</Label>
          <textarea data-testid="contract-cfg-footer_text" value={cfg.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} rows={4} className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2 text-sm" />
        </div>
      </div>
      <Button data-testid="contract-cfg-save" onClick={save} disabled={saving} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Guardar</>}
      </Button>
    </div>
  );
}

const normalizeInventoryText = (value) => String(value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

function InventoryTab({ canManage, canPurchase }) {
  const { admin } = useAuth();
  const isAdmin = admin?.role === "admin";
  const [buying, setBuying] = useState(false);
  const [rows, setRows] = useState(null);
  const [sum, setSum] = useState(null);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ status: "", source: "", model: "", capacity: "", condition: "", sim_type: "", exit_reason: "", owner_id: "" });
  const [genFilter, setGenFilter] = useState("all");
  const invGenOf = (m) => { const x = String(m || "").match(/iPhone\s+(\d+)/i); return x ? Number(x[1]) : 0; };
  const [sort, setSort] = useState("newest");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingInv, setDeletingInv] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(qInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [qInput]);

  const emptyForm = { model: "", capacity: "", color: "", imei: "", battery_percent: "", charge_cycles: "", condition: "", damages: "", has_box: "no", sim_type: "", acquisition_cost: "", owner_id: "", supplier: "", origin_country: "", entry_date: "", notes: "", price_mode: "auto", list_price: "" };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [owners, setOwners] = useState([]);
  const [priceRef, setPriceRef] = useState({});
  useEffect(() => { adminApi.get("/config").then((r) => setCatalog(r.data)).catch(() => {}); adminApi.get("/admin/owners").then((r) => setOwners(r.data)).catch(() => {}); adminApi.get("/admin/price-reference").then((r) => { const m = {}; (r.data || []).forEach((x) => { m[`${x.model}__${x.capacity}`] = x.market_high; }); setPriceRef(m); }).catch(() => {}); }, []);
  const suggestedFor = (model, capacity) => priceRef[`${model}__${capacity}`];
  const selModel = catalog?.models?.find((m) => m.name === form.model) || null;
  const selOwner = owners.find((o) => o.id === form.owner_id) || null;
  const showProvider = !form.owner_id || selOwner?.code === "RR"; // Proveedor solo si dueño = PuntoCell (RR)
  const ownerLabel = (id) => { const o = owners.find((x) => x.id === id); return o ? `${o.name} (${o.code})` : null; };
  const [photoCounts, setPhotoCounts] = useState({});
  const loadCounts = (list) => {
    const ids = (list || []).map((x) => x.id).filter(Boolean);
    if (!ids.length) { setPhotoCounts({}); return; }
    adminApi.get(`/admin/photos/counts?kind=used&ids=${ids.join(",")}`).then((r) => setPhotoCounts(r.data || {})).catch(() => {});
  };
  const load = () => {
    adminApi.get("/admin/inventory").then((r) => { setRows(r.data); loadCounts(r.data); }).catch(() => toast.error("No se pudo cargar el inventario"));
    adminApi.get("/admin/inventory/summary").then((r) => setSum(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const indexedRows = useMemo(() => (rows || []).map((item) => ({
    item,
    text: normalizeInventoryText([item.id, item.imei, item.model, item.capacity, item.color, item.sim_type, item.source, item.status, item.entry_date, item.owner_name, item.owner_code, item.supplier].filter(Boolean).join(" ")),
  })), [rows]);
  const filterOptions = useMemo(() => {
    const options = {};
    for (const field of ["source", "model", "capacity", "condition", "sim_type", "exit_reason"]) {
      options[field] = [...new Set((rows || []).map((item) => item[field]).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es", { numeric: true }));
    }
    options.status = ["DISPONIBLE", "RESERVADO", "VENDIDO", "RETIRADO"];
    return options;
  }, [rows]);
  const visibleRows = useMemo(() => {
    const terms = normalizeInventoryText(q).split(/\s+/).filter(Boolean);
    const result = indexedRows.filter(({ item, text }) => terms.every((term) => text.includes(term)) && (genFilter === "all" || invGenOf(item.model) === Number(genFilter)) && Object.entries(filters).every(([field, value]) => !value || (value === "__missing__" ? !item[field] : item[field] === value))).map(({ item }) => item);
    return result.sort((a, b) => {
      if (sort.startsWith("model")) return a.model.localeCompare(b.model, "es", { numeric: true }) * (sort === "model_asc" ? 1 : -1);
      if (sort.startsWith("cost")) {
        if (a.acquisition_cost == null) return b.acquisition_cost == null ? 0 : 1;
        if (b.acquisition_cost == null) return -1;
        return (Number(a.acquisition_cost) - Number(b.acquisition_cost)) * (sort === "cost_asc" ? 1 : -1);
      }
      return ((Date.parse(a.created_at || a.entry_date) || 0) - (Date.parse(b.created_at || b.entry_date) || 0)) * (sort === "oldest" ? 1 : -1);
    });
  }, [indexedRows, filters, q, sort, genFilter]);

  const invGens = useMemo(() => [...new Set((rows || []).map((it) => invGenOf(it.model)))].filter((n) => n > 0).sort((a, b) => b - a), [rows]);

  const activeFilters = Object.values(filters).filter(Boolean).length;
  const hasActiveFilterOrSearch = Boolean(qInput || q || activeFilters > 0);

  const resetFilters = () => {
    setQInput("");
    setQ("");
    setFilters({ status: "", source: "", model: "", capacity: "", condition: "", sim_type: "", exit_reason: "", owner_id: "" });
    setGenFilter("all");
    setSort("newest");
  };

  const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(0)}`);
  const STATUS_STYLE = { DISPONIBLE: "bg-emerald-100 text-emerald-700", RESERVADO: "bg-amber-100 text-amber-700", VENDIDO: "bg-red-100 text-red-700", RETIRADO: "bg-zinc-200 text-zinc-600" };
  const changeStatus = async (iid, st) => {
    try { await adminApi.patch(`/admin/inventory/${iid}/status`, { status: st }); toast.success(`Estado: ${st}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const updateSim = async (iid, sim) => {
    try { await adminApi.put(`/admin/inventory/${iid}`, { sim_type: sim }); toast.success("Tipo de SIM actualizado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar el Tipo de SIM"); }
  };
  const updateOwner = async (iid, oid) => {
    try { await adminApi.put(`/admin/inventory/${iid}`, { owner_id: oid || null }); toast.success("Dueño actualizado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar el dueño"); }
  };
  const priceOf = (it) => (it.price_mode === "manual" && it.list_price != null) ? Number(it.list_price) : suggestedFor(it.model, it.capacity);
  const reclassifyNew = async (it) => {
    if (!window.confirm(`¿Mover ${it.model} ${it.capacity || ""} (${it.id}) a iPhone Nuevos? Desaparecerá de Usados y aparecerá una sola vez en Nuevos, conservando dueño y costo.`)) return;
    try { const { data } = await adminApi.post(`/admin/inventory/${it.id}/reclassify-as-new`); toast.success(`Reclasificado a iPhone Nuevos: ${data.new_id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo reclasificar"); }
  };
  const _selOwnerAdd = owners.find((o) => o.id === form.owner_id) || null;
  const _extConsAdd = !!_selOwnerAdd && _selOwnerAdd.code !== "RR";
  const _effModeAdd = form.settlement_mode || _selOwnerAdd?.settlement_mode || "FIXED_OWNER_PRICE";
  const _isPostAdd = _effModeAdd === "POST_SALE_SETTLEMENT";
  const _isCpAdd = _effModeAdd === "COST_PLUS_PROFIT_SHARE";
  const addItem = async () => {
    if (!form.model || !form.capacity) { toast.error("Selecciona modelo y capacidad"); return; }
    if (form.battery_percent !== "" && (Number(form.battery_percent) < 0 || Number(form.battery_percent) > 100)) { toast.error("Batería debe estar entre 0 y 100"); return; }
    if (form.imei && !/^\d{15}$/.test(form.imei)) { toast.error("IMEI debe tener 15 dígitos"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        damages: form.damages || null,
        sim_type: form.sim_type || null,
        has_box: form.has_box === "si",
        battery_percent: form.battery_percent ? Number(form.battery_percent) : null,
        charge_cycles: form.charge_cycles ? Number(form.charge_cycles) : null,
        entry_date: form.entry_date ? new Date(form.entry_date).toISOString() : null,
      };
      if (_extConsAdd) {
        payload.settlement_mode = _effModeAdd;
        payload.acquisition_cost = null;
        payload.owner_cost = _isPostAdd ? null : (form.owner_cost ? Number(form.owner_cost) : null);
        payload.profit_share_pct = _isCpAdd ? (form.profit_share_pct ? Number(form.profit_share_pct) : null) : null;
        payload.list_price = form.list_price !== "" && form.list_price != null ? Number(form.list_price) : null;
        payload.price_mode = payload.list_price != null ? "manual" : "auto";
      } else {
        payload.acquisition_cost = form.acquisition_cost ? Number(form.acquisition_cost) : null;
        payload.price_mode = form.price_mode === "manual" ? "manual" : "auto";
        payload.list_price = form.price_mode === "manual" && form.list_price !== "" ? Number(form.list_price) : null;
      }
      await adminApi.post("/admin/inventory", payload);
      toast.success("Equipo agregado al inventario"); setAdding(false);
      setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo agregar"); } finally { setSaving(false); }
  };
  if (!rows) return <Loading />;
  return (
    <div data-testid="inventory-panel" className="space-y-4">
      {sum && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {[["📦 Disponibles", sum.available_units], ["💰 Costo disp.", money(sum.available_cost)], ["🏬 Mercancía", sum.manual_units], ["🔄 Trade-In", sum.trade_in_units], ["💵 Compra directa", sum.direct_units], ["✅ Vendidos", sum.sold_units], ["📱 Total", sum.total_units]].map(([l, v], i) => (
            <div key={i} className="rounded-2xl border border-zinc-200 p-3" data-testid={`inv-kpi-${i}`}>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400">{l}</p>
              <p className="text-lg font-semibold text-zinc-900">{v}</p>
            </div>
          ))}
        </div>
      )}
      <section className="rounded-2xl border border-zinc-200 bg-zinc-50/80 p-3 space-y-2" aria-label="Buscar y filtrar inventario" data-testid="inv-toolbar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-[1_1_100%] md:flex-[1_1_250px]">
            <label htmlFor="inv-search" className="sr-only">Buscar equipo</label>
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              id="inv-search"
              data-testid="inv-search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Buscar por ID, IMEI, modelo, capacidad, color o SIM..."
              autoComplete="off"
              className="h-10 rounded-xl bg-white pl-9 pr-9 text-xs sm:text-sm border-zinc-200 focus-visible:ring-1 focus-visible:ring-zinc-400"
            />
            {qInput && (
              <button
                type="button"
                onClick={() => { setQInput(""); setQ(""); }}
                aria-label="Borrar búsqueda"
                data-testid="inv-search-clear"
                className="absolute right-0 top-0 grid h-10 w-9 place-items-center text-zinc-400 hover:text-zinc-700 rounded-xl focus-visible:ring-2 focus-visible:ring-zinc-400"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

            <select
              aria-label="Estado"
              data-testid="inv-filter-status"
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              className="h-10 min-w-0 flex-[1_1_110px] md:flex-none md:w-[124px] rounded-xl border border-zinc-200 bg-white px-2 text-xs font-medium text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
            >
              <option value="">Estado: Todos</option>
              {filterOptions.status.map((st) => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>

            <select
              aria-label="Dueño"
              data-testid="inv-filter-owner"
              value={filters.owner_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, owner_id: e.target.value }))}
              className="h-10 min-w-0 flex-[1_1_130px] md:flex-none md:w-[150px] rounded-xl border border-zinc-200 bg-white px-2 text-xs font-medium text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
            >
              <option value="">Dueño: Todos</option>
              {owners.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.code})</option>)}
            </select>

            <select
              aria-label="Ordenar por"
              data-testid="inv-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="h-10 min-w-0 flex-[1_1_170px] md:flex-none md:w-[180px] rounded-xl border border-zinc-200 bg-white px-2 text-xs font-medium text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
            >
              <option value="newest">Ordenar: Últimos agregados</option>
              <option value="oldest">Ordenar: Más antiguos</option>
              <option value="model_asc">Modelo A-Z</option>
              <option value="model_desc">Modelo Z-A</option>
              <option value="cost_asc">Menor costo</option>
              <option value="cost_desc">Mayor costo</option>
            </select>

            {/* 4. FILTROS AVANZADOS */}
            <Button
              type="button"
              variant={activeFilters > 0 ? "default" : "outline"}
              data-testid="inv-filters-toggle"
              aria-expanded={filtersOpen}
              aria-controls="inv-filters"
              onClick={() => setFiltersOpen((open) => !open)}
              className={`h-10 rounded-xl px-3 text-xs font-medium ${
                activeFilters > 0
                  ? "bg-zinc-900 text-white hover:bg-zinc-800"
                  : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-100"
              }`}
            >
              <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
              Filtros{activeFilters > 0 ? ` (${activeFilters})` : ""}
            </Button>

            {/* 5. LIMPIAR FILTROS */}
            {hasActiveFilterOrSearch && (
              <Button
                type="button"
                variant="ghost"
                data-testid="inv-reset-filters"
                onClick={resetFilters}
                className="h-10 rounded-xl px-3 text-xs text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200/60"
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Limpiar filtros
              </Button>
            )}
        </div>

        {/* PANEL DE FILTROS AVANZADOS */}
        <div
          id="inv-filters"
          data-testid="inv-filters"
          className={`${filtersOpen ? "block" : "hidden"} border-t border-zinc-200 pt-2 space-y-2`}
        >
          <div className="flex items-center justify-between pb-2 border-b border-zinc-100">
            <span data-testid="inv-filters-heading" className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
              <SlidersHorizontal className="w-3.5 h-3.5 text-zinc-500" /> Filtros avanzados
            </span>
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              data-testid="inv-filters-close"
              aria-label="Cerrar filtros avanzados"
              className="h-8 px-2 rounded-lg text-zinc-500 hover:text-zinc-700 focus-visible:ring-2 focus-visible:ring-zinc-400 text-xs flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" /> Cerrar
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[
              ["source", "Fuente"],
              ["model", "Modelo"],
              ["capacity", "Capacidad"],
              ["condition", "Condición"],
              ["sim_type", "Tipo de SIM"],
              ["exit_reason", "Motivo de salida"],
            ].map(([field, label]) => (
              <div key={field} className="flex flex-col gap-1 min-w-0">
                <span className="text-[11px] font-medium text-zinc-500 truncate">{label}</span>
                <select
                  data-testid={`inv-filter-${field}`}
                  aria-label={label}
                  title={filters[field] === "__missing__" ? "Sin especificar" : filters[field] || "Todos"}
                  value={filters[field]}
                  onChange={(e) => setFilters((prev) => ({ ...prev, [field]: e.target.value }))}
                  className="h-10 w-full min-w-0 rounded-lg border border-zinc-200 bg-white px-2 text-xs font-normal text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                >
                  <option value="">Todos</option>
                  {filterOptions[field].map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                  {["condition", "sim_type"].includes(field) && rows.some((item) => !item[field]) && (
                    <option value="__missing__">Sin especificar</option>
                  )}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* FILTRO RÁPIDO POR MODELO (reutiliza el patrón de chips de Venta Simple) */}
        {invGens.length > 0 && (
          <div className="flex gap-1.5 overflow-x-auto px-1 pb-1" data-testid="inv-gen-filters">
            <button data-testid="inv-gen-all" onClick={() => setGenFilter("all")} className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-medium transition-colors ${genFilter === "all" ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"}`}>Todos</button>
            {invGens.map((g) => (
              <button key={g} data-testid={`inv-gen-${g}`} onClick={() => setGenFilter(String(g))} className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-medium transition-colors ${genFilter === String(g) ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"}`}>{g}</button>
            ))}
          </div>
        )}

        {/* RESUMEN DE RESULTADOS */}
        <div className="text-xs text-zinc-500 px-1">
          <p role="status" aria-live="polite" data-testid="inv-results-count">
            Mostrando <strong className="font-semibold text-zinc-900">{visibleRows.length}</strong> de {rows.length} equipos
            {activeFilters > 0 ? ` · ${activeFilters} filtro${activeFilters === 1 ? "" : "s"} activo${activeFilters === 1 ? "" : "s"}` : ""}
          </p>
        </div>
      </section>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={load} className="h-11 rounded-xl" data-testid="inv-refresh"><RotateCcw className="mr-2 h-4 w-4" />Actualizar</Button>
        {canPurchase && <Button variant="outline" onClick={() => setBuying(true)} className="h-11 rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="inv-buy-toggle">Compra directa</Button>}
        {canManage && <Button onClick={() => setAdding((v) => !v)} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800" data-testid="inv-add-toggle">Agregar equipo</Button>}
      </div>
      {canPurchase && <DirectPurchaseDialog open={buying} onOpenChange={setBuying} onCreated={load} sourceLabel="Entrada por COMPRA DIRECTA a un cliente." />}
      {adding && canManage && (
        <div className="rounded-2xl border border-zinc-200 p-4 space-y-3" data-testid="inv-add-form">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">📦 Entrada manual — mercancía / proveedor</p>
          {!catalog && <p className="text-xs text-zinc-400">Cargando catálogo…</p>}
          {catalog && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">📱 Modelo *</span>
              <ModelCombobox testid="inv-add-model" value={form.model} models={catalog.models} onChange={(name) => setForm({ ...form, model: name, capacity: "", color: "" })} /></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">💾 Capacidad *</span>
              <select data-testid="inv-add-capacity" value={form.capacity} disabled={!selModel} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm disabled:opacity-50">
                <option value="">{selModel ? "Seleccionar capacidad" : "Primero elige modelo"}</option>
                {(selModel?.capacities || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">🎨 Color</span>
              <select data-testid="inv-add-color" value={form.color} disabled={!selModel || !form.capacity} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm disabled:opacity-50">
                <option value="">{form.capacity ? "Seleccionar color" : "Primero elige capacidad"}</option>
                {(selModel?.colors || []).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">🔢 IMEI (15 dígitos)</span>
              <Input data-testid="inv-add-imei" inputMode="numeric" maxLength={15} placeholder="IMEI" value={form.imei} onChange={(e) => setForm({ ...form, imei: e.target.value.replace(/\D/g, "") })} className="h-10 rounded-xl" /></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">🔋 Batería %</span>
              <Input type="number" min="0" max="100" data-testid="inv-add-battery_percent" placeholder="0–100" value={form.battery_percent} onChange={(e) => setForm({ ...form, battery_percent: e.target.value })} className="h-10 rounded-xl" /></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">🔄 Ciclos</span>
              <Input type="number" min="0" data-testid="inv-add-charge_cycles" placeholder="Ciclos" value={form.charge_cycles} onChange={(e) => setForm({ ...form, charge_cycles: e.target.value })} className="h-10 rounded-xl" /></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">📋 Condición</span>
              <select data-testid="inv-add-condition" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm">
                <option value="">Seleccionar condición</option>
                {catalog.aesthetic_conditions.map((a) => <option key={a.key} value={a.label}>{a.label}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">⚠️ Daños</span>
              <select data-testid="inv-add-damages" value={form.damages} onChange={(e) => setForm({ ...form, damages: e.target.value })} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm">
                <option value="">Seleccionar daño</option>
                {catalog.damages.map((d) => <option key={d.key} value={d.label}>{d.label}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">📦 Caja</span>
              <select data-testid="inv-add-has_box" value={form.has_box} onChange={(e) => setForm({ ...form, has_box: e.target.value })} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm">
                <option value="no">Sin caja</option>
                <option value="si">Con caja original</option>
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">📶 Tipo de SIM</span>
              <select data-testid="inv-add-sim_type" value={form.sim_type} onChange={(e) => setForm({ ...form, sim_type: e.target.value })} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm">
                <option value="">Seleccionar tipo de SIM</option>
                {(catalog.sim_types || []).map((s) => <option key={s.key} value={s.label}>{s.label}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">💰 Costo $</span>
              <Input type="number" min="0" data-testid="inv-add-acquisition_cost" placeholder="Costo" value={form.acquisition_cost} onChange={(e) => setForm({ ...form, acquisition_cost: e.target.value })} className="h-10 rounded-xl" /></label>
            {_extConsAdd ? (
            <div className="col-span-2 sm:col-span-3 rounded-xl border border-indigo-200 bg-indigo-50/60 p-3 space-y-2" data-testid="inv-add-cons-block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-indigo-600">Consignación · {_selOwnerAdd.name}</span>
              <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-500 px-1">Modalidad</span>
                <select data-testid="inv-add-mode" value={_effModeAdd} onChange={(e) => setForm({ ...form, settlement_mode: e.target.value })} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm bg-white">
                  <option value="FIXED_OWNER_PRICE">Precio fijo — el dueño fija cuánto recibir</option>
                  <option value="COST_PLUS_PROFIT_SHARE">Capital + participación en ganancia</option>
                  <option value="POST_SALE_SETTLEMENT">Liquidación posterior — se acuerda tras vender</option>
                </select></label>
              {!_isPostAdd && (
                <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-500 px-1">{_isCpAdd ? "Costo del consignador $" : "Precio del consignador (lo que recibe) $"}</span>
                  <Input data-testid="inv-add-owner_cost" type="number" min="0" value={form.owner_cost ?? ""} onChange={(e) => setForm({ ...form, owner_cost: e.target.value })} className="h-10 rounded-xl bg-white" /></label>
              )}
              {_isCpAdd && (
                <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-500 px-1">% participación del consignador</span>
                  <Input data-testid="inv-add-pct" type="number" min="0" max="100" value={form.profit_share_pct ?? ""} onChange={(e) => setForm({ ...form, profit_share_pct: e.target.value })} className="h-10 rounded-xl bg-white" /></label>
              )}
              {_isPostAdd && <p className="text-[11px] text-indigo-600">No se declara costo. Se publica y vende; el reparto se acuerda después desde Consignadores → Ventas.</p>}
              <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-500 px-1">Precio de publicación $</span>
                <Input data-testid="inv-add-pub_price" type="number" min="0" value={form.list_price ?? ""} onChange={(e) => setForm({ ...form, list_price: e.target.value })} className="h-10 rounded-xl bg-white" /></label>
            </div>
            ) : (
            <div className="col-span-2 sm:col-span-3 rounded-xl border border-zinc-200 bg-zinc-50/70 p-3 space-y-2" data-testid="inv-add-price-block">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Precio de venta</span>
                <span className="text-xs text-zinc-500">Sugerido por fórmula: <b className="text-zinc-900" data-testid="inv-add-suggested">{suggestedFor(form.model, form.capacity) != null ? money(suggestedFor(form.model, form.capacity)) : "—"}</b></span>
              </div>
              <div className="flex gap-2">
                <button type="button" data-testid="inv-add-price-auto" onClick={() => setForm({ ...form, price_mode: "auto" })} className={`h-9 flex-1 rounded-lg text-xs font-medium border transition-colors ${form.price_mode !== "manual" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>● Automático</button>
                <button type="button" data-testid="inv-add-price-manual" onClick={() => setForm({ ...form, price_mode: "manual", list_price: form.list_price || (suggestedFor(form.model, form.capacity) ?? "") })} className={`h-9 flex-1 rounded-lg text-xs font-medium border transition-colors ${form.price_mode === "manual" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>○ Manual</button>
              </div>
              {form.price_mode === "manual" ? (
                <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">Precio de venta (publicado) $</span>
                  <Input type="number" min="0" data-testid="inv-add-list_price" placeholder="Precio manual" value={form.list_price} onChange={(e) => setForm({ ...form, list_price: e.target.value })} className="h-10 rounded-xl bg-white" /></label>
              ) : (
                <p className="text-xs text-zinc-600" data-testid="inv-add-price-display">Precio publicado: <b className="text-zinc-900">{suggestedFor(form.model, form.capacity) != null ? money(suggestedFor(form.model, form.capacity)) : "—"}</b> <span className="text-zinc-400">(automático)</span></p>
              )}
            </div>
            )}
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">👤 Dueño / Consignador</span>
              <select data-testid="inv-add-owner" value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value, supplier: (owners.find((o) => o.id === e.target.value)?.code === "RR" || !e.target.value) ? form.supplier : "" })} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm">
                <option value="">PuntoCell (RR) — por defecto</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.code}){o.active ? "" : " · inactivo"}</option>)}
              </select></label>
            {showProvider && (
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">Proveedor</span>
              <Input data-testid="inv-add-supplier" placeholder="Proveedor" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="h-10 rounded-xl" /></label>
            )}
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">País / origen</span>
              <Input data-testid="inv-add-origin_country" placeholder="País/origen" value={form.origin_country} onChange={(e) => setForm({ ...form, origin_country: e.target.value })} className="h-10 rounded-xl" /></label>
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">Fecha de entrada</span>
              <Input type="date" data-testid="inv-add-entry_date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} className="h-10 rounded-xl" /></label>
          </div>
          )}
          <Input data-testid="inv-add-notes" placeholder="Notas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-10 rounded-xl" />
          <Button onClick={addItem} disabled={saving || !form.model || !form.capacity} className="h-10 rounded-xl bg-zinc-900 hover:bg-zinc-800 w-full" data-testid="inv-add-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar equipo"}</Button>
        </div>
      )}
      {visibleRows.length === 0 && <div className="rounded-2xl border border-dashed border-zinc-200 py-8 px-4 text-center" data-testid="inv-empty-state"><p className="text-sm text-zinc-600">{rows.length ? "No hay equipos que coincidan con tu búsqueda y filtros." : "Sin equipos en el inventario."}</p>{(q || activeFilters > 0) && <Button variant="outline" onClick={resetFilters} data-testid="inv-empty-reset" className="mt-3 h-11 rounded-xl">Ver todos los equipos</Button>}</div>}
      <div className="space-y-2" data-testid="inv-results">
        {visibleRows.map((it) => (
          <div key={it.id} data-testid={`inv-item-${it.id}`} className="rounded-2xl border border-zinc-200 p-4 flex flex-wrap items-center gap-x-6 gap-y-2 [&>div]:min-w-0 [&>div]:max-w-full [&>div]:break-words">
            <InvThumb model={it.model} color={it.color} />
            <div className="basis-full sm:basis-[180px] flex-1">
              <p className="font-semibold text-zinc-900">{it.model} <span className="text-zinc-400 font-normal text-sm">{it.id}</span></p>
              <p className="text-xs text-zinc-500">{[it.capacity, it.color].filter(Boolean).join(" · ")}{it.imei ? ` · IMEI ${it.imei}` : ""}</p>
              <p className="text-xs text-zinc-500">{it.battery_percent != null ? `🔋 ${it.battery_percent}%` : ""}{it.charge_cycles != null ? ` · 🔄 ${it.charge_cycles}` : ""}{it.sim_type ? ` · 📶 ${it.sim_type}` : ""}</p>
              <p className="text-xs text-zinc-500" data-testid={`inv-owner-${it.id}`}>{it.owner_name ? `👤 ${it.owner_name} (${it.owner_code})` : ""}{(it.owner_code === "RR" && it.supplier) ? ` · 🏷️ ${it.supplier}` : ""}</p>
              {isExternalOwner(it.owner_code) && <p className="text-xs mt-0.5" data-testid={`inv-mode-${it.id}`}><span className="rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 text-[10px] font-semibold">{modeLabel(it.settlement_mode)}{it.settlement_mode === MODE_CP && it.profit_share_pct != null ? ` · ${it.profit_share_pct}%` : ""}</span>{it.settlement_mode === MODE_POST ? <span className="text-zinc-500 ml-1">— se acuerda tras vender</span> : (it.owner_cost != null ? <span className="text-zinc-500 ml-1">Acordado {money(it.owner_cost)}</span> : null)}</p>}
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-zinc-900" data-testid={`inv-cost-${it.id}`}>{money(it.acquisition_cost)}</p>
              <p className="text-[10px] text-zinc-400">costo · {it.source}</p>
              {it.source === "COMPRA DIRECTA" && it.source_id && (
                <div className="mt-1 flex justify-end" data-testid={`inv-contract-${it.id}`}>
                  <ContractButton operationType="purchase" operation={{ id: it.source_id, model: it.model, capacity: it.capacity, color: it.color, imei: it.imei, price_paid: it.acquisition_cost, customer: {} }} />
                </div>
              )}
            </div>
            {it.status === "VENDIDO" && (
              <div className="text-right" data-testid={`inv-sale-info-${it.id}`}>
                <p className="text-sm font-semibold text-zinc-900">Venta {money(it.sale_price)}</p>
                <p className={`text-xs font-semibold ${Number(it.profit ?? (it.sale_price != null ? it.sale_price - (it.acquisition_cost || 0) : 0)) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  Utilidad {it.sale_price != null ? money(it.profit ?? it.sale_price - (it.acquisition_cost || 0)) : "—"}
                </p>
                <p className="text-[10px] text-zinc-400">{it.exit_reason || "—"} · {(it.sold_at || it.exit_date || "").slice(0, 16).replace("T", " ")}{it.sold_by || it.updated_by ? ` · ${it.sold_by || it.updated_by}` : ""}</p>
              </div>
            )}
            <div className="text-right">
              <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATUS_STYLE[it.status] || "bg-zinc-100"}`} data-testid={`inv-status-${it.id}`}>{it.status}</span>
              <p className="text-[10px] text-zinc-400 mt-0.5">{(it.entry_date || "").slice(0, 10)}</p>
              {canManage && <button onClick={() => setEditing(it)} className="mt-1 inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900" data-testid={`inv-edit-${it.id}`}><Pencil className="w-3 h-3" /> Editar</button>}
              {canManage && <button onClick={() => setDeletingInv(it)} className="mt-1 ml-2 inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-rose-600" data-testid={`inv-delete-${it.id}`}><Trash2 className="w-3 h-3" /> Eliminar</button>}
              {canManage && it.status === "DISPONIBLE" && <button onClick={() => reclassifyNew(it)} className="mt-1 block ml-auto inline-flex items-center gap-1 text-[11px] text-emerald-600 hover:text-emerald-700" data-testid={`inv-reclassify-${it.id}`}><RotateCcw className="w-3 h-3" /> Reclasificar como Nuevo</button>}
            </div>
            <div className="basis-full sm:basis-auto flex flex-col sm:flex-row gap-2 sm:justify-end" data-testid={`inv-actions-${it.id}`}>
              <DevicePhotos scope="admin" kind="used" deviceId={it.id} canUpload count={photoCounts[it.id] || 0} label="Fotos reales" buttonClassName="w-full sm:w-auto justify-center" />
              <Button variant="outline" size="sm" onClick={() => printUsedTicket(it)} className="h-8 w-full sm:w-auto rounded-lg text-[11px]" data-testid={`inv-print-${it.id}`}><Printer className="w-3.5 h-3.5 mr-1" /> Info papelito</Button>
            </div>
            {canManage && (
              <select data-testid={`inv-owner-select-${it.id}`} value={it.owner_id || ""} onChange={(e) => updateOwner(it.id, e.target.value)} title="Dueño / Consignador" className="h-11 w-full sm:w-auto max-w-full rounded-xl border border-zinc-200 text-xs px-2">
                <option value="">👤 Dueño: sin definir</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.code}){o.active ? "" : " · inactivo"}</option>)}
              </select>
            )}
            {canManage && (
              <select data-testid={`inv-sim-select-${it.id}`} value={it.sim_type || ""} onChange={(e) => updateSim(it.id, e.target.value)} title="Tipo de SIM (corregir/verificar)" className="h-11 w-full sm:w-auto max-w-full rounded-xl border border-zinc-200 text-xs px-2">
                <option value="">📶 SIM: sin definir</option>
                {(catalog?.sim_types || []).map((s) => <option key={s.key} value={s.label}>{s.label}</option>)}
              </select>
            )}
            {canManage && it.status !== "VENDIDO" && (
              <select data-testid={`inv-status-select-${it.id}`} value={it.status} onChange={(e) => changeStatus(it.id, e.target.value)} className="h-9 rounded-xl border border-zinc-200 text-xs px-2">
                {["DISPONIBLE", "RESERVADO", "RETIRADO"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>
        ))}
      </div>
      {editing && canManage && <EditInventoryDialog item={editing} catalog={catalog} owners={owners} onClose={() => setEditing(null)} onSaved={load} />}
      {deletingInv && canManage && <DeleteDeviceDialog kind="used" item={deletingInv} onClose={() => setDeletingInv(null)} onDeleted={() => { setDeletingInv(null); load(); }} />}
    </div>
  );
}

function StaffPanel() {
  const { admin, logout } = useAuth();
  const perms = admin?.permissions || [];
  const has = (p) => perms.includes(p);
  const navigate = useNavigate();
  const doLogout = () => { logout(); navigate("/admin/login", { replace: true }); };
  const roleLabel = admin?.role === "manager" ? "Manager" : "Vendedor";
  const canEditPrices = has("prices:edit");
  const tabs = [];
  if (has("quotes:view")) tabs.push("quotes");
  if (has("quotes:manage") && has("inventory:view")) tabs.push("manual-tradein");
  tabs.push("reference");
  if (has("purchases:view")) tabs.push("purchases");
  if (has("inventory:view")) tabs.push("inventory");
  if (has("new_inventory:view")) tabs.push("new-iphones");
  if (has("accessories:view")) tabs.push("accessories");
  if (has("shipments:receive")) tabs.push("shipments");
  if (has("withdrawals:view") || has("cash_withdrawals:view")) tabs.push("withdrawals");
  if (has("sales:manage")) tabs.push("simple-sale");
  if (has("sales:manage")) tabs.push("warranty");
  if (canEditPrices) tabs.push("prices");
  if (has("reports:view")) { tabs.push("sales"); tabs.push("reports"); }
  tabs.unshift("inicio");
  if (has("approvals:manage")) tabs.splice(1, 0, "approvals");
  const [tab, setTab] = useState(tabs[0] || "reference");
  const labels = { inicio: "Inicio", approvals: "Por aprobar", quotes: "Cotizaciones", "manual-tradein": "Trade-In manual", reference: "Precios", purchases: "Compra directa", inventory: "iPhone Usados", "new-iphones": "iPhone Nuevos", accessories: "Accesorios", shipments: "Recepción de mercancía", withdrawals: "Retiros", "simple-sale": "Venta simple", warranty: "Garantías y Contratos", prices: "Editar precios", sales: "Ventas", reports: "Informes" };
  const navItems = tabs.map((id) => [id, labels[id]]);
  const quickActions = [["simple-sale", "Venta simple", ShoppingCart], ["manual-tradein", "Trade-In", RotateCcw], ["inventory", "iPhone Usados", Boxes]].filter(([id]) => tabs.includes(id));

  return (
    <div className="min-h-screen bg-[#f7f7f8] text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-slate-200/80 shadow-sm">
        <div className="px-4 sm:px-6 h-16 flex items-center justify-between">
          <button type="button" onClick={() => setTab("inicio")} data-testid="header-logo-home" aria-label="Ir a Inicio" className="flex items-center gap-2.5 rounded-lg transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400">
            <img src="/puntocell-logo.png" alt="PuntoCell" className="h-8 w-auto max-w-[150px] object-contain" />
            <span className="text-xs uppercase tracking-widest font-semibold text-slate-400 border-l border-slate-200 pl-2.5" data-testid="staff-role-label">{roleLabel}</span>
          </button>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-500">{admin?.email}</span>
            <Button variant="outline" onClick={doLogout} data-testid="logout-button" className="h-10 rounded-lg border-slate-200 text-slate-700 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200">
              <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>
      <AdminNavigation items={navItems} value={tab} onChange={setTab} />
      <main className="md:pl-64 pb-[calc(76px+env(safe-area-inset-bottom))] md:pb-0">
        <div className="mx-auto max-w-4xl px-5 py-7">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsContent value="inicio"><HomeDashboard onNavigate={setTab} allowed={new Set(tabs)} /></TabsContent>
          {has("approvals:manage") && <TabsContent value="approvals"><ApprovalsTab /></TabsContent>}
          {has("quotes:view") && <TabsContent value="quotes"><QuotesTab canManage={has("quotes:manage")} /></TabsContent>}
          {has("quotes:manage") && has("inventory:view") && <TabsContent value="manual-tradein"><ManualTradeInTab /></TabsContent>}
          <TabsContent value="reference"><PriceReferenceTab quickActions={quickActions} onNavigate={setTab} /></TabsContent>
          {has("purchases:view") && <TabsContent value="purchases"><PurchasesTab canManage={has("purchases:manage")} /></TabsContent>}
          {has("inventory:view") && <TabsContent value="inventory"><InventoryTab canManage={has("inventory:manage")} canPurchase={has("purchases:manage")} /></TabsContent>}
          {has("new_inventory:view") && <TabsContent value="new-iphones"><NewIPhonesTab isAdmin={admin?.role === "admin"} canManage={has("new_inventory:manage")} /></TabsContent>}
          {has("accessories:view") && <TabsContent value="accessories"><AccessoriesTab isAdmin={admin?.role === "admin"} canManage={has("accessories:manage")} /></TabsContent>}
          {has("shipments:receive") && <TabsContent value="shipments"><ShipmentsAdminTab /></TabsContent>}
          {(has("withdrawals:view") || has("cash_withdrawals:view")) && <TabsContent value="withdrawals"><RetirosPanel canViewMerch={has("withdrawals:view")} canConfirmMerch={has("withdrawals:confirm")} canViewCash={has("cash_withdrawals:view")} canConfirmCash={has("cash_withdrawals:confirm")} /></TabsContent>}
          {has("sales:manage") && <TabsContent value="simple-sale"><SimpleSaleTab /></TabsContent>}
          {has("sales:manage") && <TabsContent value="warranty"><WarrantyContractsTab /></TabsContent>}
          {canEditPrices && <TabsContent value="prices"><PricesTab /></TabsContent>}
          {has("reports:view") && <TabsContent value="sales"><SalesTab /></TabsContent>}
          {has("reports:view") && <TabsContent value="reports"><ReportsTab /></TabsContent>}
        </Tabs>
        </div>
      </main>
    </div>
  );
}

function PriceReferenceTab({ quickActions = [], onNavigate }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [cap, setCap] = useState("all");
  const [filterGen, setFilterGen] = useState("all");
  const [sortNewest, setSortNewest] = useState(true);
  const [threshold, setThreshold] = useState(null);
  useEffect(() => { adminApi.get("/admin/price-reference").then((r) => setRows(r.data)).catch(() => toast.error("No se pudieron cargar los precios")); }, []);
  useEffect(() => { adminApi.get("/admin/rules").then((r) => setThreshold(r.data.battery_threshold)).catch(() => {}); }, []);
  if (!rows) return <Loading />;
  const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(0)}`);
  const caps = [...new Set(rows.map((r) => r.capacity))];
  const term = q.trim().toLowerCase();
  const filtered = rows.filter((r) => (term === "" || r.model.toLowerCase().includes(term)) && (cap === "all" || r.capacity === cap));
  const groups = [];
  filtered.forEach((r) => { let g = groups.find((x) => x.model === r.model); if (!g) { g = { model: r.model, items: [] }; groups.push(g); } g.items.push(r); });
  const genOf = (model) => { const m = model.match(/iPhone\s+(\d+)/i); return m ? Number(m[1]) : 0; };
  groups.forEach((g) => { g.gen = genOf(g.model); });
  const gens = [...new Set(rows.map((r) => genOf(r.model)))].filter((n) => n > 0).sort((a, b) => b - a);
  let visibleGroups = filterGen === "all" ? groups : groups.filter((g) => String(g.gen) === filterGen);
  visibleGroups = [...visibleGroups].sort((a, b) => (sortNewest ? b.gen - a.gen : a.gen - b.gen));

  return (
    <div data-testid="price-reference-panel" className="space-y-4">
      {quickActions.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="price-ref-quick-access">
          <h2 className="text-xl font-semibold text-zinc-900">Precios</h2>
          <div className="flex flex-wrap gap-2">
            {quickActions.map(([id, label, Icon]) => (
              <Button key={id} variant="outline" size="sm" onClick={() => onNavigate?.(id)} data-testid={`quick-access-${id}`} className="h-9 rounded-full border-zinc-200 px-3.5 text-sm font-medium hover:bg-zinc-900 hover:text-white">
                <Icon className="w-4 h-4 mr-1.5" />{label}
              </Button>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-500">
        Referencia de compra (solo lectura). Valores para equipo en <strong>mejor estado</strong>, batería sobre el umbral y sin caja. Fuente: precios vigentes del sistema.
      </div>
      <div className="flex flex-col sm:flex-row gap-2 sticky top-16 z-10">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="price-ref-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar modelo (ej. 17 Pro Max)" className="h-11 rounded-xl pl-9 bg-white" />
        </div>
        <select data-testid="price-ref-capacity" value={cap} onChange={(e) => setCap(e.target.value)} className="h-11 rounded-xl border border-zinc-200 px-3 text-sm bg-white">
          <option value="all">Todas las capacidades</option>
          {caps.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="space-y-2" data-testid="price-ref-toolbar">
        <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
          <button
            data-testid="price-ref-gen-all"
            onClick={() => setFilterGen("all")}
            className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-medium transition-colors ${filterGen === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
          >Todos</button>
          {gens.map((g) => (
            <button
              key={g}
              data-testid={`price-ref-gen-${g}`}
              onClick={() => setFilterGen(String(g))}
              className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-medium transition-colors ${filterGen === String(g) ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            >{g}</button>
          ))}
        </div>
        <div className="flex w-fit rounded-full bg-zinc-100 p-1">
          <button
            data-testid="price-ref-sort-newest"
            onClick={() => setSortNewest(true)}
            className={`h-7 px-3 rounded-full text-xs font-medium transition-colors ${sortNewest ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"}`}
          >Más nuevos primero</button>
          <button
            data-testid="price-ref-sort-oldest"
            onClick={() => setSortNewest(false)}
            className={`h-7 px-3 rounded-full text-xs font-medium transition-colors ${!sortNewest ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"}`}
          >Más antiguos primero</button>
        </div>
      </div>
      {visibleGroups.length === 0 && <p className="text-sm text-zinc-400 py-6 text-center">Sin resultados.</p>}
      <div className="space-y-3">
        {visibleGroups.map((g) => (
          <div key={g.model} data-testid={`price-ref-model-${slug(g.model)}`} className="rounded-2xl border border-zinc-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-zinc-900 text-white text-sm font-semibold">{g.model}</div>
            <div className="divide-y divide-zinc-100">
              {g.items.map((r) => (
                <div key={r.capacity} data-testid={`price-ref-row-${slug(g.model)}-${slug(r.capacity)}`} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-zinc-800">{r.capacity}</span>
                    {!r.available && <span className="text-xs text-amber-600 font-medium">Precio pendiente</span>}
                  </div>
                  {r.available && (
                    <>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div className="rounded-xl border border-zinc-200 bg-white px-3 py-2.5">
                          <p className="text-[11px] font-medium leading-tight text-zinc-500">Batería {threshold ?? "—"}% o más</p>
                          <p className="mt-0.5 text-lg font-semibold tracking-tight text-zinc-900" data-testid={`price-ref-market-high-${slug(g.model)}-${slug(r.capacity)}`}>{money(r.market_high)}</p>
                        </div>
                        <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5">
                          <p className="text-[11px] font-medium leading-tight text-zinc-500">Batería menor a {threshold ?? "—"}%</p>
                          <p className="mt-0.5 text-lg font-semibold tracking-tight text-zinc-900" data-testid={`price-ref-market-low-${slug(g.model)}-${slug(r.capacity)}`}>{money(r.market_low)}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-zinc-400">
                        <span>Trade-In <span className="font-semibold text-zinc-600" data-testid={`price-ref-tradein-${slug(g.model)}-${slug(r.capacity)}`}>{money(r.trade_in)}</span></span>
                        <span>Compra directa <span className="font-semibold text-emerald-600" data-testid={`price-ref-cash-${slug(g.model)}-${slug(r.capacity)}`}>{money(r.cash)}</span></span>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ label, value, strong }) {  return (
    <div className="flex items-center justify-between py-2.5 border-b border-zinc-100 last:border-0">
      <span className="text-sm text-zinc-500">{label}</span>
      <span className={`text-sm text-right ${strong ? "font-semibold text-zinc-900" : "font-medium text-zinc-800"}`}>{value}</span>
    </div>
  );
}

function Loading() {
  return <div className="py-14 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
}
